import { FILL_DEFAULTS, resolveFillSettings, isFillPass } from "../SvgEditor/helpers/fillSettings";
import { cleanSatinElement } from "../SvgEditor/helpers/cleanSatinElement";
import { measureSatinColumnWidthMm, satinUnderlayAttributes } from "../SvgEditor/helpers/satinUnderlay";
import { runningStitchAttributes } from "../SvgEditor/helpers/runningStitchAttributes";
import { useState, useEffect, useRef, useMemo, useCallback, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { message, Modal } from "antd";
import { emptySatinLayers } from "./emptySatinLayers";
import { IonModal, IonContent } from "@ionic/react";
import { useWindowSize } from "usehooks-ts";
import { stringify } from "svgson";
import { parseEmbroideryFile, subsampleStitches } from "../SvgEditor/components/stitchParser";
import injectEmbroideryAttributes, { SATIN_UNDERLAP_MM, SATIN_PULL_COMP_MM } from "../SvgEditor/helpers/injectEmbroideryAttributes";
import { applyFabricCompensation } from "./fabricCompensation";
import {
  alignRailDirections,
  findNearestPoint as findNearestPointOnPath,
  splitAtIndices,
  validateSplitDistance,
  generateSatinBorderRails,
  samplePath,
} from "../SvgEditor/helpers/splitPathToRails";
import { loadStitchFileFromStorage } from "../../lib/firebase/firebase";
import StitchCanvas from "./StitchCanvas";
import { useRailDrawing } from "./useRailDrawing";
import useLayerColorHistory from "./useLayerColorHistory";
import StitchToolbar from "./StitchToolbar";
import StitchSidebar from "./StitchSidebar";
import StitchExportButton from "./StitchExportButton";
import SatinJobQueue from "./SatinJobQueue";
import { nextLayerRailsFromSatinResult, unfinishedSatinLabel } from "./satinUnresolvedRetry";
import {
  cornerRailNodeAt,
  parseEditableRailPath,
  railNodesFromPoints,
  railNodesToD,
  reverseEditableRailPath,
  reverseRailNodes,
  simplifyRailPoints,
  smoothRailNodeAt,
  smoothRailNodes,
} from "./railPathEditing";
import { getEmbroideryServiceUrl } from "../../config/embroideryServiceUrl";
import { trackFileConversion } from "../../lib/analytics/metaPixel";
import { useAuth } from "../../contexts/AuthContext";
import { isNativeiOS } from "../../lib/utils/platform";
import { isEmbroideryTrace } from "../../config/appVariant";
import IOSPaywall from "../PaywallModal/IOSPaywall";
import PaywallModal from "../PaywallModal/PaywallModal";
const HOOP_SIZES = [
  { label: "4×4 in (100×100mm)", width: 100, height: 100 },
  { label: "5×7 in (130×180mm)", width: 130, height: 180 },
  { label: "6×10 in (160×260mm)", width: 160, height: 260 },
  { label: "8×8 in (200×200mm)", width: 200, height: 200 },
  { label: "Custom", width: null, height: null },
];

// Brother (and most home) machines refuse to display a design whose stitched
// extents reach the hoop's nominal edge — their usable sewing field is a few mm
// smaller than the hoop's marketed size (a "100mm" 4×4 hoop only sews ~99mm).
// Pull-compensation + expand + satin underlay also push stitches slightly past
// the SVG bounds. Fit designs into the hoop minus this margin (per side) so the
// generated file always clears the limit and stays visible on the machine.
const HOOP_SAFETY_MARGIN_MM = 2;
const safeHoopDim = (mm) => Math.max(10, mm - HOOP_SAFETY_MARGIN_MM * 2);

// Satin-column width thresholds (mm) used to pick an underlay strategy that
// matches how a professional digitizer would stitch each column. See
// measureSatinColumnWidthMm / buildSatinAttrs for how these are applied.
const SATIN_PASS_DEFAULTS = {
  pull_compensation_mm: SATIN_PULL_COMP_MM,
  push_compensation_mm: 0,
  zigzag_spacing_mm: 0.4,
  max_stitch_length_mm: 7.0,
  split_method: "staggered",
  split_staggers: 4,
  short_stitch_distance_mm: 0.25,
  short_stitch_inset: 15,
  fill_underlay: true,
  satin_underlay_profile: "auto",
  contour_underlay_inset_mm: 0.4,
  zigzag_underlay_spacing_mm: 3.0,
  reverse_rails: "automatic",
  swap_satin_rails: false,
};


const STITCH_TYPES_MAP = {
  auto_fill: "auto_fill",
  cross_stitch: "cross_stitch",
  meander: "meander_fill",
};

const VALID_FILL_METHODS = new Set(["auto_fill", "legacy_fill", "contour_fill", "circular_fill", "meander_fill"]);
const VALID_AI_STITCH_TYPES = new Set(["auto_fill", "legacy_fill", "contour_fill", "circular_fill", "meander_fill"]);
// Keep auto_fill as auto_fill — it generally produces smoother routing and
// cleaner quality stitch-outs than legacy_fill for filled regions.
const sanitizeAiStitchType = (type) => {
  return VALID_AI_STITCH_TYPES.has(type) ? type : "auto_fill";
};

const StitchPreviewCanvas = ({ realStitchData, colors: layerColors }) => {
  const canvasRef = useRef(null);
  const CANVAS_SIZE = 480;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !realStitchData?.stitches?.length) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    canvas.width = CANVAS_SIZE * dpr;
    canvas.height = CANVAS_SIZE * dpr;
    ctx.scale(dpr, dpr);

    ctx.fillStyle = "#faf8f5";
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    const { minX, maxX, minY, maxY, stitches, colors: stitchColors } = realStitchData;
    const dataW = maxX - minX || 1;
    const dataH = maxY - minY || 1;
    const pad = 40;
    const area = CANVAS_SIZE - pad * 2;
    const scale = Math.min(area / dataW, area / dataH);
    const offX = pad + (area - dataW * scale) / 2;
    const offY = pad + (area - dataH * scale) / 2;

    const toX = (x) => offX + (x - minX) * scale;
    const toY = (y) => offY + (y - minY) * scale;

    const palette = (layerColors?.length ? layerColors : null) || stitchColors || ["#333"];
    let lastColor = -1;

    ctx.lineWidth = 1.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    const sampled = subsampleStitches(stitches, 30000);
    for (let i = 0; i < sampled.length; i++) {
      const s = sampled[i];
      if (s.colorIndex !== lastColor) {
        if (lastColor !== -1) ctx.stroke();
        lastColor = s.colorIndex;
        ctx.beginPath();
        ctx.strokeStyle = palette[s.colorIndex % palette.length] || "#333";
        ctx.moveTo(toX(s.x), toY(s.y));
        continue;
      }
      if (s.jump) {
        ctx.moveTo(toX(s.x), toY(s.y));
      } else {
        ctx.lineTo(toX(s.x), toY(s.y));
      }
    }
    ctx.stroke();
  }, [realStitchData, layerColors]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: "100%", maxWidth: CANVAS_SIZE, aspectRatio: "1", borderRadius: "12px", border: "1px solid #eee", display: "block", margin: "0 auto" }}
    />
  );
};

// Bounding box of an SVG path d-string by sampling on-curve coordinates only.
// We intentionally skip control points (after C/c, S/s, Q/q) because those can
// extend far beyond the rendered curve. Sweeps only commands that produce on-path
// coordinates: M m L l H h V v A a Z z and the endpoint pairs of C/S/Q/T (etc).
function pathBoundingBox(d) {
  if (!d) return null;
  const tokens = d.match(/([MmLlHhVvCcSsQqTtAaZz])|(-?\d*\.?\d+(?:[eE][-+]?\d+)?)/g);
  if (!tokens) return null;
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  let cmd = "M", cx = 0, cy = 0, startX = 0, startY = 0, hasPoint = false;
  let i = 0;
  const isCmd = (t) => /^[A-Za-z]$/.test(t);
  const num = () => parseFloat(tokens[i++]);
  const upd = (x, y) => {
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
    hasPoint = true;
  };
  while (i < tokens.length) {
    if (isCmd(tokens[i])) { cmd = tokens[i++]; if (cmd === "M") cmd = "M"; else if (cmd === "m") cmd = "m"; }
    if (i >= tokens.length) break;
    const isRel = cmd === cmd.toLowerCase();
    const c = cmd.toUpperCase();
    if (c === "Z") { cx = startX; cy = startY; continue; }
    if (c === "M" || c === "L" || c === "T") {
      const x = num(), y = num();
      cx = isRel ? cx + x : x; cy = isRel ? cy + y : y;
      if (c === "M" && !hasPoint) { startX = cx; startY = cy; }
      upd(cx, cy);
      if (c === "M") cmd = isRel ? "l" : "L";
    } else if (c === "H") { const x = num(); cx = isRel ? cx + x : x; upd(cx, cy); }
    else if (c === "V") { const y = num(); cy = isRel ? cy + y : y; upd(cx, cy); }
    else if (c === "C") { i += 4; const x = num(), y = num(); cx = isRel ? cx + x : x; cy = isRel ? cy + y : y; upd(cx, cy); }
    else if (c === "S" || c === "Q") { i += 2; const x = num(), y = num(); cx = isRel ? cx + x : x; cy = isRel ? cy + y : y; upd(cx, cy); }
    else if (c === "A") { i += 5; const x = num(), y = num(); cx = isRel ? cx + x : x; cy = isRel ? cy + y : y; upd(cx, cy); }
    else { i++; }
  }
  if (!hasPoint || !isFinite(minX)) return null;
  return { minX, maxX, minY, maxY };
}

function filterSvgToViewBox(svgJson) {
  if (!svgJson?.attributes?.viewBox) return svgJson;
  const vbParts = svgJson.attributes.viewBox.split(/[\s,]+/).map(Number).filter((n) => !isNaN(n));
  if (vbParts.length !== 4) return svgJson;
  const [vx, vy, vw, vh] = vbParts;
  const vRight = vx + vw, vBottom = vy + vh;
  const cloned = JSON.parse(JSON.stringify(svgJson));
  let removed = 0;
  const filterChildren = (node) => {
    if (!node?.children) return node;
    node.children = node.children.filter((c) => {
      if (c.name === "path" && c.attributes?.d) {
        const bb = pathBoundingBox(c.attributes.d);
        if (!bb) return true;
        // Only remove paths that are entirely outside the viewBox (no overlap
        // in at least one axis). The previous 10% area threshold was too
        // aggressive for thin/narrow subpaths near the boundary.
        if (bb.maxX < vx || bb.minX > vRight || bb.maxY < vy || bb.minY > vBottom) {
          removed++;
          return false;
        }
      }
      return true;
    }).map(filterChildren);
    return node;
  };
  filterChildren(cloned);
  if (removed > 0) console.log(`[StitchEditor] Filtered ${removed} out-of-viewBox path(s) from SVG`);
  return cloned;
}

// Remap an index-keyed object ({ "0": ..., "1": ... }) so its keys follow a
// layer move. Mirrors the same shift logic used inside the embroidery hook so
// every layer-keyed store ends up consistent after a reorder.
const remapLayerKeyedObject = (obj, fromIdx, toIdx) => {
  if (!obj) return obj;
  const reordered = {};
  for (const [k, val] of Object.entries(obj)) {
    const idx = parseInt(k, 10);
    if (Number.isNaN(idx)) { reordered[k] = val; continue; }
    let newIdx = idx;
    if (idx === fromIdx) newIdx = toIdx;
    else if (fromIdx < toIdx && idx > fromIdx && idx <= toIdx) newIdx = idx - 1;
    else if (fromIdx > toIdx && idx >= toIdx && idx < fromIdx) newIdx = idx + 1;
    reordered[newIdx] = val;
  }
  return reordered;
};

const remapLayerSelection = (sel, fromIdx, toIdx) => {
  if (typeof sel !== "number") return sel;
  if (sel === fromIdx) return toIdx;
  if (fromIdx < toIdx && sel > fromIdx && sel <= toIdx) return sel - 1;
  if (fromIdx > toIdx && sel >= toIdx && sel < fromIdx) return sel + 1;
  return sel;
};

// Layers that belong to the same color "group" share this attribute. The first
// member of a group is the parent (carries the color swatch + Add Sublayer
// control); the rest render nested beneath it. Stored on the SVG node so it
// persists with the design and survives save/reload.
const SUBLAYER_GROUP_ATTR = "data-sublayer-group";

// Derive, for each layer index, its grouping metadata for the sidebar.
const computeLayerMeta = (children) => {
  const list = Array.isArray(children) ? children : [];
  // First index seen for each group id → that layer is the group "parent".
  const firstByGroup = {};
  const seqByGroup = {};
  return list.map((child, i) => {
    const groupId = child?.attributes?.[SUBLAYER_GROUP_ATTR] || null;
    if (!groupId) {
      return { groupId: null, isSublayer: false, isGroupParent: false, subIndex: 0, hasGroup: false };
    }
    if (firstByGroup[groupId] === undefined) {
      firstByGroup[groupId] = i;
      seqByGroup[groupId] = 0;
      return { groupId, isSublayer: false, isGroupParent: true, subIndex: 0, hasGroup: true };
    }
    seqByGroup[groupId] += 1;
    return { groupId, isSublayer: true, isGroupParent: false, subIndex: seqByGroup[groupId], hasGroup: true };
  });
};

const StitchEditor = ({
  svgJoined: rawSvgJoined,
  svgSplit: rawSvgSplit,
  colors,
  setColors,
  updateSvg,
  embroidery,
  fileName,
  fileId,
  stitchAiAnalysisCacheRef,
  embroideryForAiRef,
  savedStitchState,
  stitchStateLoaded,
  initialAiAnalysis,
  // Previewing somebody else's community design. Export still works; the
  // parent just doesn't persist anything. See StitchEditorPage.
  readOnly = false,
  onAiAnalysisReady,
  onStitchFileGenerated,
  onConversionStart,
  onConversionEnd,
  pendingConversion,
  autoRestartedRef,
  layerSelected,
  setLayerSelected,
  onUpdateLayerPath,
  onSvgUndo,
  onSvgRedo,
  initialDstBase64,
  referenceArtwork,
  onRetraceArtwork,
  onDownloaded,
}) => {
  const svgJoined = useMemo(() => filterSvgToViewBox(rawSvgJoined), [rawSvgJoined]);
  const svgSplit = useMemo(() => filterSvgToViewBox(rawSvgSplit), [rawSvgSplit]);

  const { user, userIsPremium, userIsEmbroideryPremium } = useAuth();
  const effectivePremium = isEmbroideryTrace ? userIsEmbroideryPremium : userIsPremium;
  const [showToolbarPaywall, setShowToolbarPaywall] = useState(false);
  const [showToolbarIOSPaywall, setShowToolbarIOSPaywall] = useState(false);
  const pendingExportRef = useRef(null);

  // Responsive flags — mirror the SVG editor so the Stitch UI collapses to a
  // bottom toolbar + slide-down Layers panel on phones, and uses the touch
  // layout (no hover tooltips) on tablets.
  // We use matchMedia("(pointer: coarse)") for the isTablet flag (suppresses
  // hover tooltips on iPad), but keep isPhone as a pure width check so iPad
  // continues to get the desktop layout.
  const { width: screenWidth } = useWindowSize();
  const isCoarsePointer =
    typeof window !== "undefined" &&
    !!window.matchMedia?.("(pointer: coarse)")?.matches;
  const isTablet = isCoarsePointer || screenWidth < 900;
  const isPhone = screenWidth > 0 && screenWidth < 768;
  const [layersPanelOpen, setLayersPanelOpen] = useState(!isTablet);
  const [referenceArtworkVisible, setReferenceArtworkVisible] = useState(false);
  const [referenceArtworkOpacity, setReferenceArtworkOpacity] = useState(0.35);

  const [selectedFormat, setSelectedFormat] = useState("dst");
  const [isConverting, setIsConverting] = useState(false);
  const [conversionResult, setConversionResult] = useState(null);
  const [realStitchData, setRealStitchData] = useState(null);
  const [error, setError] = useState(null);
  const [convertStatus, setConvertStatus] = useState("");
  const abortRef = useRef(null);

  // Persistence status for the most recent stitch file. "saved" once the
  // upload + Firestore reference write completes, "saving" while it's in
  // flight, "error" if the upload threw, and "idle" before the user has
  // generated anything in this session. Surfaced via StitchExportButton so the
  // user gets a quiet confirmation that their result is on their account.
  const [stitchFileSaveStatus, setStitchFileSaveStatus] = useState("idle");
  const [savedFileMeta, setSavedFileMeta] = useState(null);
  // Track which (data, format) tuple has already been uploaded so that a
  // re-render doesn't re-upload an unchanged result.
  const lastUploadKeyRef = useRef(null);


  const cacheEntry = stitchAiAnalysisCacheRef?.current;
  const cacheHit = fileId && cacheEntry?.fileId === fileId && cacheEntry?.analysis;

  const [aiAnalysis, setAiAnalysis] = useState(() => {
    if (cacheHit) return cacheEntry.analysis;
    if (savedStitchState?.aiAnalysis) return savedStitchState.aiAnalysis;
    if (initialAiAnalysis) return initialAiAnalysis;
    return null;
  });
  // Only true while the analysis API call is in flight — not while waiting for Firestore.
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [topbarExportSlot, setTopbarExportSlot] = useState(null);

  useLayoutEffect(() => {
    setTopbarExportSlot(document.getElementById("stitch-topbar-export-slot"));
  }, []);

  const openExportModal = useCallback(() => {
    if (aiLoading) return;
    if (effectivePremium) {
      setExportModalOpen(true);
      return;
    }
    pendingExportRef.current = () => setExportModalOpen(true);
    if (isNativeiOS()) {
      setShowToolbarIOSPaywall(true);
    } else {
      setShowToolbarPaywall(true);
    }
  }, [aiLoading, effectivePremium]);

  // Pixel-erase "Cut" tool state
  const [eraseActive, setEraseActive] = useState(false);
  const [smartCutActive, setSmartCutActive] = useState(false);
  const [smartCutLayerIdx, setSmartCutLayerIdx] = useState(null);
  const [eraseLayerIdx, setEraseLayerIdx] = useState(null);
  const cutToolRef = useRef(null);
  const fillToolRef = useRef(null);
  const pixelEraseToolRef = useRef(null);
  const eraseDraftHistoryRef = useRef({ canUndo: false, canRedo: false, undo: null, redo: null });
  const [eraseDraftHistory, setEraseDraftHistory] = useState({ canUndo: false, canRedo: false });

  // Paint-bucket "Fill" tool state
  const [fillActive, setFillActive] = useState(false);
  const [fillLayerIdx, setFillLayerIdx] = useState(null);

  // Pixel-by-pixel brush eraser state
  const [pixelEraseActive, setPixelEraseActive] = useState(false);
  const [pixelEraseLayerIdx, setPixelEraseLayerIdx] = useState(null);

  // Pixel-by-pixel brush draw state (additive twin of the brush eraser)
  const [drawActive, setDrawActive] = useState(false);
  const [drawLayerIdx, setDrawLayerIdx] = useState(null);
  const drawToolRef = useRef(null);

  // Expand/Shrink (dilate/erode) tool state — slider-driven layer offset
  const [morphActive, setMorphActive] = useState(false);
  const [morphLayerIdx, setMorphLayerIdx] = useState(null);
  const morphToolRef = useRef(null);

  // Whether the Stitch settings panel is open — the satin rail guides only show
  // while it is, so they don't clutter the canvas during normal viewing.
  const [stitchPanelOpen, setStitchPanelOpen] = useState(false);
  const [satinRetryRequest, setSatinRetryRequest] = useState(null);

  const handleEraseDraftHistoryChange = useCallback((state) => {
    const next = state || { canUndo: false, canRedo: false, undo: null, redo: null };
    eraseDraftHistoryRef.current = next;
    setEraseDraftHistory((prev) => (
      prev.canUndo === !!next.canUndo && prev.canRedo === !!next.canRedo
        ? prev
        : { canUndo: !!next.canUndo, canRedo: !!next.canRedo }
    ));
  }, []);

  const handleEraseStart = useCallback((layerIdx) => {
    handleEraseDraftHistoryChange(null);
    setEraseLayerIdx(layerIdx);
    setEraseActive(true);
  }, [handleEraseDraftHistoryChange]);

  const closeCutTool = useCallback(() => {
    handleEraseDraftHistoryChange(null);
    setEraseActive(false);
    setEraseLayerIdx(null);
    setSmartCutActive(false);
    setSmartCutLayerIdx(null);
  }, [handleEraseDraftHistoryChange]);

  const leaveCutTool = useCallback(async () => {
    // Paint + brush-erase accumulate edits and only auto-apply when the tool is
    // switched away. Commit any pending work here, then deactivate. Only one of
    // these tools is ever active at a time.
    if (fillActive) {
      const fill = fillToolRef.current;
      if (fill?.hasWorkToApply?.()) {
        const ok = await fill.applyAndExit();
        if (!ok) return false;
      }
      setFillActive(false);
      setFillLayerIdx(null);
    }
    if (pixelEraseActive) {
      const pe = pixelEraseToolRef.current;
      if (pe?.hasWorkToApply?.()) {
        const ok = await pe.applyAndExit();
        if (!ok) return false;
      }
      setPixelEraseActive(false);
      setPixelEraseLayerIdx(null);
    }
    if (drawActive) {
      const dr = drawToolRef.current;
      if (dr?.hasWorkToApply?.()) {
        const ok = await dr.applyAndExit();
        if (!ok) return false;
      }
      setDrawActive(false);
      setDrawLayerIdx(null);
    }
    if (morphActive) {
      const mo = morphToolRef.current;
      if (mo?.hasWorkToApply?.()) {
        const ok = await mo.applyAndExit();
        if (!ok) return false;
      }
      setMorphActive(false);
      setMorphLayerIdx(null);
    }

    if (smartCutActive) {
      setSmartCutActive(false);
      setSmartCutLayerIdx(null);
      return true;
    }
    if (!eraseActive) return true;
    const cut = cutToolRef.current;
    if (cut?.hasWorkToApply?.()) {
      const ok = await cut.applyAndExit();
      if (!ok) return false;
      return true;
    }
    closeCutTool();
    return true;
  }, [eraseActive, smartCutActive, fillActive, pixelEraseActive, drawActive, morphActive, closeCutTool]);

  const withLeaveCut = useCallback((fn) => async (...args) => {
    const ok = await leaveCutTool();
    if (ok) fn(...args);
  }, [leaveCutTool]);

  const handleLayerChange = useCallback(async (idx) => {
    // Paint, brush-erase, and cut overlays accumulate edits and remount when
    // their layer changes, so commit any pending work on the layer we're
    // leaving first. If a commit fails (e.g. vectorization error), stay put so
    // nothing is lost.
    if (fillActive && fillToolRef.current?.hasWorkToApply?.()) {
      const ok = await fillToolRef.current.applyAndExit();
      if (!ok) return;
    }
    if (pixelEraseActive && pixelEraseToolRef.current?.hasWorkToApply?.()) {
      const ok = await pixelEraseToolRef.current.applyAndExit();
      if (!ok) return;
    }
    if (drawActive && drawToolRef.current?.hasWorkToApply?.()) {
      const ok = await drawToolRef.current.applyAndExit();
      if (!ok) return;
    }
    if (morphActive && morphToolRef.current?.hasWorkToApply?.()) {
      const ok = await morphToolRef.current.applyAndExit();
      if (!ok) return;
    }
    if (eraseActive && cutToolRef.current?.hasWorkToApply?.()) {
      const ok = await cutToolRef.current.applyAndExit();
      if (!ok) return;
    }

    // Non-numeric selection (e.g. "all"): exit the per-layer tools entirely.
    if (typeof idx !== "number") {
      setLayerSelected?.(idx);
      if (fillActive) { setFillActive(false); setFillLayerIdx(null); }
      if (pixelEraseActive) { setPixelEraseActive(false); setPixelEraseLayerIdx(null); }
      if (drawActive) { setDrawActive(false); setDrawLayerIdx(null); }
      if (morphActive) { setMorphActive(false); setMorphLayerIdx(null); }
      if (smartCutActive) { setSmartCutActive(false); setSmartCutLayerIdx(null); }
      if (eraseActive) closeCutTool();
      return;
    }

    // Re-target every active tool to the new layer so it keeps operating on the
    // layer the user is now looking at (previously only paint/erase followed,
    // and the cut tool's index was left stale when it had pending work).
    setLayerSelected?.(idx);
    if (fillActive) setFillLayerIdx(idx);
    if (pixelEraseActive) setPixelEraseLayerIdx(idx);
    if (drawActive) setDrawLayerIdx(idx);
    if (morphActive) setMorphLayerIdx(idx);
    if (smartCutActive) setSmartCutLayerIdx(idx);
    if (eraseActive) {
      handleEraseDraftHistoryChange(null);
      setEraseLayerIdx(idx);
    }
  }, [
    setLayerSelected,
    fillActive,
    pixelEraseActive,
    drawActive,
    morphActive,
    eraseActive,
    smartCutActive,
    closeCutTool,
    handleEraseDraftHistoryChange,
  ]);

  const handleEraseCancel = useCallback(() => {
    closeCutTool();
  }, [closeCutTool]);

  const handleEraseComplete = useCallback((layerIdx, newPathD) => {
    handleEraseDraftHistoryChange(null);
    if (onUpdateLayerPath) {
      onUpdateLayerPath(layerIdx, newPathD || "");
      embroidery?.pushSvgCutMarker?.();
    }
    setEraseActive(false);
    setEraseLayerIdx(null);
  }, [onUpdateLayerPath, embroidery, handleEraseDraftHistoryChange]);

  const handleCutMarksChange = useCallback((layerIdx, marks) => {
    embroidery?.setLayerCutMarksForLayer?.(layerIdx, marks);
  }, [embroidery]);

  // Paint-bucket "Fill" tool handlers
  const handleFillStart = useCallback((layerIdx) => {
    setFillLayerIdx(layerIdx);
    setFillActive(true);
  }, []);

  const guardedEraseStart = useMemo(
    () => withLeaveCut(handleEraseStart),
    [withLeaveCut, handleEraseStart],
  );

  const guardedFillStart = useMemo(
    () => withLeaveCut(handleFillStart),
    [withLeaveCut, handleFillStart],
  );

  // Commit a paint pass — update path and keep the tool open. Pending paints are
  // only flushed here on an explicit Apply or when switching tools.
  const handleFillApply = useCallback((layerIdx, newPathD) => {
    if (!onUpdateLayerPath) return;
    onUpdateLayerPath(layerIdx, newPathD || "");
    embroidery?.pushSvgCutMarker?.();
  }, [onUpdateLayerPath, embroidery]);

  // Pixel-by-pixel brush eraser handlers
  const handlePixelEraseStart = useCallback((layerIdx) => {
    setPixelEraseLayerIdx(layerIdx);
    setPixelEraseActive(true);
  }, []);

  const guardedPixelEraseStart = useMemo(
    () => withLeaveCut(handlePixelEraseStart),
    [withLeaveCut, handlePixelEraseStart],
  );

  // Auto-apply: update path but keep tool open so user can keep erasing
  const handlePixelEraseApply = useCallback((layerIdx, newPathD) => {
    if (!onUpdateLayerPath) return;
    onUpdateLayerPath(layerIdx, newPathD || "");
    embroidery?.pushSvgCutMarker?.();
  }, [onUpdateLayerPath, embroidery]);

  // Pixel-by-pixel brush draw handlers (additive twin of brush erase)
  const handleDrawStart = useCallback((layerIdx) => {
    setDrawLayerIdx(layerIdx);
    setDrawActive(true);
  }, []);

  const guardedDrawStart = useMemo(
    () => withLeaveCut(handleDrawStart),
    [withLeaveCut, handleDrawStart],
  );

  // Auto-apply: update path but keep tool open so user can keep drawing
  const handleDrawApply = useCallback((layerIdx, newPathD) => {
    if (!onUpdateLayerPath) return;
    onUpdateLayerPath(layerIdx, newPathD || "");
    embroidery?.pushSvgCutMarker?.();
  }, [onUpdateLayerPath, embroidery]);

  // Expand/Shrink (dilate/erode) handlers — slider-driven layer offset
  const handleMorphStart = useCallback((layerIdx) => {
    setMorphLayerIdx(layerIdx);
    setMorphActive(true);
  }, []);

  const guardedMorphStart = useMemo(
    () => withLeaveCut(handleMorphStart),
    [withLeaveCut, handleMorphStart],
  );

  // Auto-apply: update path but keep tool open so user can keep adjusting
  const handleMorphApply = useCallback((layerIdx, newPathD) => {
    if (!onUpdateLayerPath) return;
    onUpdateLayerPath(layerIdx, newPathD || "");
    embroidery?.pushSvgCutMarker?.();
  }, [onUpdateLayerPath, embroidery]);

  // Smart Cut tool — click individual subpaths to delete them from the layer
  const handleSmartCutStart = useCallback((layerIdx) => {
    setSmartCutLayerIdx(layerIdx);
    setSmartCutActive(true);
  }, []);

  const guardedSmartCutStart = useMemo(
    () => withLeaveCut(handleSmartCutStart),
    [withLeaveCut, handleSmartCutStart],
  );

  const handleSmartCutCancel = useCallback(() => {
    setSmartCutActive(false);
    setSmartCutLayerIdx(null);
  }, []);

  const handleSmartCutSubpath = useCallback((subpathIdx) => {
    if (!onUpdateLayerPath || smartCutLayerIdx == null) return;
    const layerEl = svgJoined?.children?.[smartCutLayerIdx];
    const compoundD = layerEl?.attributes?.d;
    if (!compoundD) return;
    const subpaths = compoundD.split(/(?=M)/i).filter((s) => s.trim());
    const remaining = subpaths.filter((_, i) => i !== subpathIdx);
    const newPathD = remaining.join(" ").trim();
    onUpdateLayerPath(smartCutLayerIdx, newPathD);
    embroidery?.pushSvgCutMarker?.();
    // If the layer is now empty, exit smart cut mode
    if (!newPathD) {
      setSmartCutActive(false);
      setSmartCutLayerIdx(null);
    }
  }, [onUpdateLayerPath, smartCutLayerIdx, svgJoined, embroidery]);

  const { drawing: activeRailDrawing, setDrawing: setActiveRailDrawing,
    addPoint: addRailPoint, updateLastPoint: updateLastRailPoint,
    undo: removeLastRailPoint, redo: redoRailPoint,
    canUndo: canUndoRailPoint, canRedo: canRedoRailPoint } = useRailDrawing();

  // Combined undo/redo: handles both stitch settings and SVG Cut operations
  const handleUndo = useCallback(() => {
    if (activeRailDrawing) {
      removeLastRailPoint();
      return;
    }
    const draft = eraseDraftHistoryRef.current;
    if (eraseActive && draft?.canUndo && typeof draft.undo === "function") {
      draft.undo();
      return;
    }
    const result = embroidery?.undoStitch?.();
    if (result === 'svgCut' || result === 'svgLayerDelete' || result === 'svgColor' || result === 'svgLayerReorder') onSvgUndo?.();
  }, [activeRailDrawing, removeLastRailPoint, eraseActive, embroidery, onSvgUndo]);

  const handleRedo = useCallback(() => {
    if (activeRailDrawing) {
      redoRailPoint();
      return;
    }
    const draft = eraseDraftHistoryRef.current;
    if (eraseActive && draft?.canRedo && typeof draft.redo === "function") {
      draft.redo();
      return;
    }
    const result = embroidery?.redoStitch?.();
    if (result === 'svgCut' || result === 'svgLayerDelete' || result === 'svgColor' || result === 'svgLayerReorder') onSvgRedo?.();
  }, [activeRailDrawing, redoRailPoint, eraseActive, embroidery, onSvgRedo]);

  // Global undo/redo shortcuts (Cmd/Ctrl+Z, Cmd/Ctrl+Shift+Z / Ctrl+Y).
  // StitchEraseCanvas uses capture phase when the Cut tool is active.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.target?.closest?.("input, textarea, select, ion-input, ion-textarea, [contenteditable]")) return;
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const key = e.key.toLowerCase();
      if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if (key === "y" || (key === "z" && e.shiftKey)) {
        e.preventDefault();
        handleRedo();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [handleUndo, handleRedo]);

  // Warm up the Cloud Run embroidery service on mount so it isn't cold when the user hits Convert
  useEffect(() => {
    fetch(`${getEmbroideryServiceUrl()}/health`, { method: "GET" }).catch(() => {});
  }, []);

  // Rail state: layerRails[layerIdx] = { pairs: [{ rail0, rail1 }, ...] }
  // Initialize from embroidery (hydrated from saved file state if present)
  const [layerRails, setLayerRails] = useState(() => embroidery?.layerRails || {});
  const [railEditing, setRailEditing] = useState(null);
  const [autoSatinPreview, setAutoSatinPreview] = useState(null);
  useEffect(() => setRailEditing(null), [layerSelected]);
  const updateLayerRailsWithHistory = useCallback((updater) => {
    embroidery?.recordStitchHistory?.({
      railsUpdater: typeof updater === "function" ? updater : () => updater,
    });
  }, [embroidery]);

  // Adopt external rail changes (undo/redo, hydrate, recordStitchHistory) into local
  // canvas state. Local layerRails is a read mirror — all writes go through
  // recordStitchHistory on the embroidery hook.
  const lastPushedRailsRef = useRef(null);
  useEffect(() => {
    const ext = embroidery?.layerRails;
    if (!ext) return;
    if (ext === lastPushedRailsRef.current) return;
    if (ext === layerRails) return;
    lastPushedRailsRef.current = ext;
    setLayerRails(ext);
    // Undo/redo/hydrate changed rails externally — split-handle metadata is stale.
    setAutoDetectMeta({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embroidery?.layerRails]);
  // Tracks which pair index is being built during drawing
  const pendingPairRef = useRef({});

  // Rail selection state (click-to-select paths as rails)
  const [railSelection, setRailSelection] = useState(null);

  const handleSplitRailsCreated = useCallback((layerIndex, rails) => {
    const aligned = alignRailDirections(rails.rail0, rails.rail1);
    updateLayerRailsWithHistory((prev) => {
      const existing = prev[layerIndex] || {};
      const pairs = existing.pairs ? [...existing.pairs] : [];
      pairs.push({ rail0: aligned.rail0, rail1: aligned.rail1, rungs: aligned.rungs });
      return { ...prev, [layerIndex]: { ...existing, pairs } };
    });
  }, [updateLayerRailsWithHistory]);

  const [layerPieces, setLayerPieces] = useState({});

  const handleCutFinished = useCallback((layerIndex, pieces) => {
    setLayerPieces((prev) => ({ ...prev, [layerIndex]: pieces }));
  }, []);

  const handleUndoLastSplitPair = useCallback((layerIndex) => {
    updateLayerRailsWithHistory((prev) => {
      const existing = prev[layerIndex];
      if (!existing?.pairs?.length) return prev;
      const pairs = existing.pairs.slice(0, -1);
      return { ...prev, [layerIndex]: { ...existing, pairs } };
    });
  }, [updateLayerRailsWithHistory]);

  const startRailDrawing = useCallback((layerIndex, railIndex) => {
    setRailSelection(null);
    setRailEditing(null);
    const existing = layerRails[layerIndex]?.pairs || [];
    pendingPairRef.current[layerIndex] = existing.length;
    setActiveRailDrawing({ layerIndex, railIndex, points: [] });
  }, [layerRails]);

  const startRailEditing = useCallback((layerIndex, pairIdx, railIndex) => {
    const pair = layerRails?.[layerIndex]?.pairs?.[pairIdx];
    const pathD = pair?.[`rail${railIndex}`];
    const storedNodes = pair?.[`rail${railIndex}Nodes`];
    const points = storedNodes?.length ? null : parseEditableRailPath(pathD);
    const nodes = storedNodes?.length ? storedNodes.map((node) => ({ ...node })) : railNodesFromPoints(points);
    if (!nodes.length) {
      message.info("Node editing is available for rails created with Draw Edges.");
      return;
    }
    setActiveRailDrawing(null);
    setRailSelection(null);
    setRailEditing({ layerIndex, pairIdx, railIndex, selectedNode: null, selectedNodes: [], nodes });
  }, [layerRails]);

  // Drag previews update the local read mirror for immediate feedback. The
  // pointer-up commit records one undo step instead of one step per pixel.
  const previewRailEdit = useCallback((layerIndex, pairIdx, railIndex, pathD, nodes) => {
    setRailEditing((prev) => prev && prev.layerIndex === layerIndex && prev.pairIdx === pairIdx && prev.railIndex === railIndex
      ? { ...prev, nodes }
      : prev);
    setLayerRails((prev) => {
      const entry = prev[layerIndex] || {};
      const pairs = [...(entry.pairs || [])];
      if (!pairs[pairIdx]) return prev;
      pairs[pairIdx] = { ...pairs[pairIdx], [`rail${railIndex}`]: pathD, [`rail${railIndex}Nodes`]: nodes };
      return { ...prev, [layerIndex]: { ...entry, pairs } };
    });
  }, []);

  const commitRailEdit = useCallback((layerIndex, pairIdx, railIndex, pathD, nodes) => {
    setRailEditing((prev) => prev && prev.layerIndex === layerIndex && prev.pairIdx === pairIdx && prev.railIndex === railIndex
      ? { ...prev, nodes }
      : prev);
    updateLayerRailsWithHistory((prev) => {
      const entry = prev[layerIndex] || {};
      const pairs = [...(entry.pairs || [])];
      const pair = pairs[pairIdx];
      if (!pair) return prev;
      const edited = { ...pair, [`rail${railIndex}`]: pathD, [`rail${railIndex}Nodes`]: nodes };
      if (edited.rail0 && edited.rail1) {
        const aligned = alignRailDirections(edited.rail0, edited.rail1, edited.rungs?.length || undefined);
        edited.rail0 = aligned.rail0;
        if (aligned.rail1 !== edited.rail1 && edited.rail1Nodes?.length) {
          edited.rail1Nodes = reverseRailNodes(edited.rail1Nodes);
        }
        edited.rail1 = aligned.rail1;
        edited.rungs = aligned.rungs;
      }
      pairs[pairIdx] = edited;
      return { ...prev, [layerIndex]: { ...entry, pairs } };
    });
  }, [updateLayerRailsWithHistory]);

  const insertRailNode = useCallback((segmentIndex, point) => {
    if (!railEditing) return;
    const { layerIndex, pairIdx, railIndex } = railEditing;
    const nodes = railEditing.nodes?.map((node) => ({ ...node })) || [];
    if (nodes.length < 2) return;
    nodes.splice(segmentIndex + 1, 0, railNodesFromPoints([point])[0]);
    setRailEditing((prev) => ({ ...prev, selectedNode: segmentIndex + 1, selectedNodes: [segmentIndex + 1] }));
    commitRailEdit(layerIndex, pairIdx, railIndex, railNodesToD(nodes), nodes);
  }, [railEditing, commitRailEdit]);

  const deleteSelectedRailNode = useCallback(() => {
    if (!railEditing) return;
    const { layerIndex, pairIdx, railIndex, selectedNode } = railEditing;
    const selected = new Set(railEditing.selectedNodes || (selectedNode == null ? [] : [selectedNode]));
    if (!selected.size) return;
    const nodes = railEditing.nodes.filter((_, index) => !selected.has(index));
    if (nodes.length < 2) {
      message.info("Keep at least two points on each edge. Select fewer points to delete.");
      return;
    }
    setRailEditing((prev) => ({ ...prev, selectedNode: null, selectedNodes: [] }));
    commitRailEdit(layerIndex, pairIdx, railIndex, railNodesToD(nodes), nodes);
  }, [railEditing, commitRailEdit]);

  const curveRailEdge = useCallback(() => {
    if (!railEditing?.nodes?.length) return;
    const nodes = smoothRailNodes(railEditing.nodes);
    commitRailEdit(railEditing.layerIndex, railEditing.pairIdx, railEditing.railIndex, railNodesToD(nodes), nodes);
  }, [railEditing, commitRailEdit]);

  const setSelectedRailNodeType = useCallback((type) => {
    if (!railEditing?.nodes?.length || railEditing.selectedNode == null) return;
    const nodes = type === "smooth"
      ? smoothRailNodeAt(railEditing.nodes, railEditing.selectedNode)
      : cornerRailNodeAt(railEditing.nodes, railEditing.selectedNode);
    commitRailEdit(railEditing.layerIndex, railEditing.pairIdx, railEditing.railIndex, railNodesToD(nodes), nodes);
  }, [railEditing, commitRailEdit]);

  const reverseRailColumn = useCallback(() => {
    if (!railEditing) return;
    const { layerIndex, pairIdx } = railEditing;
    updateLayerRailsWithHistory((prev) => {
      const entry = prev[layerIndex] || {};
      const pairs = [...(entry.pairs || [])];
      const pair = pairs[pairIdx];
      if (!pair) return prev;
      const rail0 = reverseEditableRailPath(pair.rail0);
      const rail1 = reverseEditableRailPath(pair.rail1);
      const nodes0 = pair.rail0Nodes?.length ? reverseRailNodes(pair.rail0Nodes) : null;
      const nodes1 = pair.rail1Nodes?.length ? reverseRailNodes(pair.rail1Nodes) : null;
      const nextRail0 = nodes0 ? railNodesToD(nodes0) : rail0;
      const nextRail1 = nodes1 ? railNodesToD(nodes1) : rail1;
      if (!nextRail0 || !nextRail1) return prev;
      pairs[pairIdx] = {
        ...pair,
        rail0: nextRail0,
        rail1: nextRail1,
        ...(nodes0 ? { rail0Nodes: nodes0 } : {}),
        ...(nodes1 ? { rail1Nodes: nodes1 } : {}),
        rungs: (pair.rungs || []).map((d) => reverseEditableRailPath(d) || d).reverse(),
      };
      return { ...prev, [layerIndex]: { ...entry, pairs } };
    });
    setRailEditing(null);
  }, [railEditing, updateLayerRailsWithHistory]);

  const finishRailDrawing = useCallback(() => {
    const drawing = activeRailDrawing;
    if (!drawing || drawing.points.length < 2) {
      setActiveRailDrawing(null);
      return;
    }
    const { layerIndex, railIndex, points } = drawing;
    const hasHandles = points.some((point) => point.handleIn || point.handleOut);
    const nodes = hasHandles ? points : railNodesFromPoints(simplifyRailPoints(points));
    const d = railNodesToD(nodes);
    const pairIdx = pendingPairRef.current[layerIndex] ?? 0;

    if (railIndex === 1) {
      // Second rail just finished — align directions and auto-generate rungs
      const rail0D = (() => {
        const pairs = layerRails[layerIndex]?.pairs || [];
        return pairs[pairIdx]?.rail0;
      })();
      if (rail0D) {
        const aligned = alignRailDirections(rail0D, d);
        updateLayerRailsWithHistory((lr) => {
          const existing = lr[layerIndex]?.pairs ? [...lr[layerIndex].pairs] : [];
          while (existing.length <= pairIdx) existing.push({});
          const rail1Nodes = aligned.rail1 === d ? nodes : reverseRailNodes(nodes);
          existing[pairIdx] = { ...existing[pairIdx], rail1: aligned.rail1, rail1Nodes, rungs: aligned.rungs };
          return { ...lr, [layerIndex]: { pairs: existing } };
        });
      } else {
        updateLayerRailsWithHistory((lr) => {
          const existing = lr[layerIndex]?.pairs ? [...lr[layerIndex].pairs] : [];
          while (existing.length <= pairIdx) existing.push({});
          existing[pairIdx] = { ...existing[pairIdx], rail1: d, rail1Nodes: nodes };
          return { ...lr, [layerIndex]: { pairs: existing } };
        });
      }
      setActiveRailDrawing(null);
    } else {
      updateLayerRailsWithHistory((lr) => {
        const existing = lr[layerIndex]?.pairs ? [...lr[layerIndex].pairs] : [];
        while (existing.length <= pairIdx) existing.push({});
        existing[pairIdx] = { ...existing[pairIdx], rail0: d, rail0Nodes: nodes };
        return { ...lr, [layerIndex]: { pairs: existing } };
      });
      setActiveRailDrawing({ layerIndex, railIndex: 1, points: [] });
    }
  }, [activeRailDrawing, layerRails, updateLayerRailsWithHistory]);

  const cancelRailDrawing = useCallback(() => {
    setActiveRailDrawing(null);
  }, []);

  const clearLayerRails = useCallback((layerIndex) => {
    updateLayerRailsWithHistory((prev) => {
      const next = { ...prev };
      delete next[layerIndex];
      return next;
    });
    setLayerPieces((prev) => {
      const next = { ...prev };
      delete next[layerIndex];
      return next;
    });
  }, [updateLayerRailsWithHistory]);

  // State for auto-detect metadata: stores sampled points + split indices per layer
  // so draggable handles can re-compute rails without re-sampling.
  const [autoDetectMeta, setAutoDetectMeta] = useState({});

  // Choosing Satin changes the stitch type; rails require an explicit action.
  const applySatinMode = useCallback((layerIndex) => {
    embroidery?.setLayerStitchType?.(layerIndex, "satin", {
      passPatch: SATIN_PASS_DEFAULTS,
    });
    setStitchSettings((p) => ({ ...p, stitch_type: "satin" }));
  }, [embroidery]);

  // Satin border: trace the selected shape's outline with a satin column by
  // generating two concentric rails offset by the border width. Reuses the same
  // rail pipeline (preview, injection, export) as the other satin methods.
  const handleCreateSatinBorder = useCallback((layerIndex, borderWidthMm = 2) => {
    const joinedChild = svgJoined?.children?.[layerIndex];
    const splitLayer = svgSplit?.children?.[layerIndex];
    const pathD = joinedChild?.attributes?.d || splitLayer?.attributes?.d;
    if (!pathD) return;

    // Convert the requested width (mm) into the SVG viewBox units the rails use.
    const vb = svgJoined?.attributes?.viewBox?.split(/[\s,]+/).map(Number).filter((v) => !isNaN(v));
    const vbW = vb && vb.length === 4 ? vb[2] : 100;
    const tW = embroidery?.targetWidth ?? 100;
    const mmToVb = vbW / (tW || 100);
    const borderWidthVb = borderWidthMm * mmToVb;

    const rails = generateSatinBorderRails(pathD, borderWidthVb);
    if (!rails) return;

    embroidery?.setLayerStitchType?.(layerIndex, "satin", {
      passPatch: SATIN_PASS_DEFAULTS,
      railsUpdater: (prev) => ({
        ...prev,
        [layerIndex]: { pairs: [{ rail0: rails.rail0, rail1: rails.rail1, rungs: rails.rungs }] },
      }),
    });
    // Concentric border rails aren't a split — drop stale split-handle metadata.
    setAutoDetectMeta((prev) => {
      const next = { ...prev };
      delete next[layerIndex];
      return next;
    });
    setStitchSettings((p) => ({ ...p, stitch_type: "satin" }));
  }, [svgJoined, svgSplit, embroidery]);

  // A3b: Re-split at new indices when user drags a handle
  const handleDragSplitPoint = useCallback((layerIndex, handleId, newX, newY) => {
    const meta = autoDetectMeta[layerIndex];
    if (!meta?.points) return;

    const hit = findNearestPointOnPath(meta.points, newX, newY);
    if (!hit) return;

    let newIdx1 = meta.idx1, newIdx2 = meta.idx2;
    if (handleId === 1) newIdx1 = hit.index;
    else newIdx2 = hit.index;

    if (!validateSplitDistance(meta.points, newIdx1, newIdx2, 0.08)) return;

    const rails = splitAtIndices(meta.points, newIdx1, newIdx2);
    if (!rails) return;

    updateLayerRailsWithHistory((prev) => ({
      ...prev,
      [layerIndex]: { pairs: [{ rail0: rails.rail0, rail1: rails.rail1 }] },
    }));
    setAutoDetectMeta((prev) => ({
      ...prev,
      [layerIndex]: { ...prev[layerIndex], idx1: newIdx1, idx2: newIdx2 },
    }));
  }, [autoDetectMeta, updateLayerRailsWithHistory]);

  const startRailSelection = useCallback((layerIndex) => {
    setRailEditing(null);
    setActiveRailDrawing(null);
    const existing = layerRails[layerIndex]?.pairs || [];
    const pairIdx = existing.length;
    setRailSelection({ layerIndex, pairIdx, selectingRail: 0, rail0PathIdx: null, rail1PathIdx: null });
  }, [layerRails]);

  const selectPathAsRail = useCallback((pathIndex) => {
    const sel = railSelection;
    if (!sel) return;
    const layerEl = svgSplit?.children?.[sel.layerIndex];
    const layerChildren = layerEl?.children;
    let d;
    if (layerChildren?.length) {
      d = layerChildren[pathIndex]?.attributes?.d;
    } else {
      const compoundD = layerEl?.attributes?.d;
      const subpaths = compoundD ? compoundD.split(/(?=M)/i).filter((s) => s.trim()) : [];
      d = subpaths[pathIndex];
    }
    if (!d) return;

    const { layerIndex, pairIdx } = sel;
    if (sel.selectingRail === 0) {
      updateLayerRailsWithHistory((lr) => {
        const existing = lr[layerIndex]?.pairs ? [...lr[layerIndex].pairs] : [];
        while (existing.length <= pairIdx) existing.push({});
        existing[pairIdx] = { ...existing[pairIdx], rail0: d };
        return { ...lr, [layerIndex]: { pairs: existing } };
      });
      setRailSelection({ ...sel, rail0PathIdx: pathIndex, selectingRail: 1 });
    } else {
      // Auto-align rail directions and generate guide rungs
      const rail0D = (() => {
        const lr = layerRails;
        const pairs = lr[layerIndex]?.pairs || [];
        return pairs[pairIdx]?.rail0;
      })();

      if (rail0D) {
        const aligned = alignRailDirections(rail0D, d);
        updateLayerRailsWithHistory((lr) => {
          const existing = lr[layerIndex]?.pairs ? [...lr[layerIndex].pairs] : [];
          while (existing.length <= pairIdx) existing.push({});
          existing[pairIdx] = {
            ...existing[pairIdx],
            rail1: aligned.rail1,
            rungs: aligned.rungs,
          };
          return { ...lr, [layerIndex]: { pairs: existing } };
        });
      } else {
        updateLayerRailsWithHistory((lr) => {
          const existing = lr[layerIndex]?.pairs ? [...lr[layerIndex].pairs] : [];
          while (existing.length <= pairIdx) existing.push({});
          existing[pairIdx] = { ...existing[pairIdx], rail1: d };
          return { ...lr, [layerIndex]: { pairs: existing } };
        });
      }
      // Keep the tool active for continuous use — re-arm a fresh pair selection
      // on the same layer so the user can keep picking satin columns without
      // reselecting the tool. They exit explicitly via Cancel.
      setRailSelection({
        layerIndex,
        pairIdx: pairIdx + 1,
        selectingRail: 0,
        rail0PathIdx: null,
        rail1PathIdx: null,
      });
    }
  }, [svgSplit, railSelection, layerRails, updateLayerRailsWithHistory]);

  const cancelRailSelection = useCallback(() => {
    setRailSelection(null);
  }, []);

  // Rung drawing state: { layerIndex, pairIdx, startPoint: {x,y} | null }
  const [activeRungDrawing, setActiveRungDrawing] = useState(null);

  const startRungDrawing = useCallback((layerIndex, pairIdx) => {
    setActiveRailDrawing(null);
    setRailSelection(null);
    setRailEditing(null);
    setActiveRungDrawing({ layerIndex, pairIdx, startPoint: null });
  }, []);

  const guardedStartRungDrawing = useMemo(
    () => withLeaveCut(startRungDrawing),
    [withLeaveCut, startRungDrawing],
  );

  const addRungPoint = useCallback((x, y) => {
    setActiveRungDrawing((prev) => {
      if (!prev) return null;
      if (!prev.startPoint) {
        return { ...prev, startPoint: { x, y } };
      }
      const d = `M${prev.startPoint.x.toFixed(1)},${prev.startPoint.y.toFixed(1)} L${x.toFixed(1)},${y.toFixed(1)}`;
      const { layerIndex, pairIdx } = prev;
      updateLayerRailsWithHistory((lr) => {
        const existing = lr[layerIndex]?.pairs ? [...lr[layerIndex].pairs] : [];
        if (!existing[pairIdx]) return lr;
        const pair = { ...existing[pairIdx] };
        pair.rungs = [...(pair.rungs || []), d];
        existing[pairIdx] = pair;
        return { ...lr, [layerIndex]: { pairs: existing } };
      });
      return { ...prev, startPoint: null };
    });
  }, [updateLayerRailsWithHistory]);

  const finishRungDrawing = useCallback(() => {
    setActiveRungDrawing(null);
  }, []);

  const clearRungs = useCallback((layerIndex, pairIdx) => {
    updateLayerRailsWithHistory((lr) => {
      const existing = lr[layerIndex]?.pairs ? [...lr[layerIndex].pairs] : [];
      if (!existing[pairIdx]) return lr;
      const pair = { ...existing[pairIdx] };
      delete pair.rungs;
      existing[pairIdx] = pair;
      return { ...lr, [layerIndex]: { pairs: existing } };
    });
  }, [updateLayerRailsWithHistory]);

  const removeRailPair = useCallback((layerIndex, pairIdx) => {
    updateLayerRailsWithHistory((lr) => {
      const existing = lr[layerIndex]?.pairs ? [...lr[layerIndex].pairs] : [];
      const pairs = existing.filter((_, i) => i !== pairIdx);
      if (pairs.length === 0) {
        const next = { ...lr };
        delete next[layerIndex];
        return next;
      }
      return { ...lr, [layerIndex]: { pairs } };
    });
  }, [updateLayerRailsWithHistory]);

  // Move a layer from one position to another, keeping the SVG, colors,
  // per-layer settings, rails, AI analysis, and the active selection in sync.
  const handleReorderLayer = useCallback((fromIdx, toIdx) => {
    if (typeof fromIdx !== "number" || typeof toIdx !== "number") return;
    if (fromIdx === toIdx) return;

    // Cancel any in-progress drawing — its layerIndex would otherwise desync.
    setActiveRailDrawing(null);
    setActiveRungDrawing(null);
    setRailSelection(null);
    setRailEditing(null);

    // Reorder the underlying SVG (children + colors) — drives svgJoined/svgSplit.
    if (typeof updateSvg === "function") {
      updateSvg((prev) => {
        if (!prev?.children) return prev;
        const len = prev.children.length;
        if (fromIdx < 0 || fromIdx >= len) return prev;
        const target = Math.max(0, Math.min(len - 1, toIdx));
        if (fromIdx === target) return prev;
        const nextChildren = [...prev.children];
        const [moved] = nextChildren.splice(fromIdx, 1);
        nextChildren.splice(target, 0, moved);
        return { ...prev, children: nextChildren };
      });
    }

    if (typeof setColors === "function" && Array.isArray(colors) && fromIdx < colors.length) {
      const target = Math.max(0, Math.min(colors.length - 1, toIdx));
      if (fromIdx !== target) {
        const next = [...colors];
        const [moved] = next.splice(fromIdx, 1);
        next.splice(target, 0, moved);
        setColors(next);
      }
    }

    // Reindex per-layer stitch settings + rails on the embroidery hook so
    // pass settings, satin rails, and rungs all follow the moved layer.
    embroidery?.handleLayerReorder?.(fromIdx, toIdx);

    // Reindex local layer-keyed state.
    setLayerRails((prev) => remapLayerKeyedObject(prev, fromIdx, toIdx));
    setLayerPieces((prev) => remapLayerKeyedObject(prev, fromIdx, toIdx));

    setAiAnalysis((prev) => {
      if (!prev?.layers || !Array.isArray(prev.layers)) return prev;
      const len = prev.layers.length;
      if (fromIdx < 0 || fromIdx >= len) return prev;
      const target = Math.max(0, Math.min(len - 1, toIdx));
      if (fromIdx === target) return prev;
      const arr = [...prev.layers];
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(target, 0, moved);
      return { ...prev, layers: arr };
    });

    if (eraseLayerIdx != null) {
      setEraseLayerIdx((prev) => remapLayerSelection(prev, fromIdx, toIdx));
    }
    if (pixelEraseLayerIdx != null) {
      setPixelEraseLayerIdx((prev) => remapLayerSelection(prev, fromIdx, toIdx));
    }
    if (drawLayerIdx != null) {
      setDrawLayerIdx((prev) => remapLayerSelection(prev, fromIdx, toIdx));
    }
    if (morphLayerIdx != null) {
      setMorphLayerIdx((prev) => remapLayerSelection(prev, fromIdx, toIdx));
    }
    if (fillLayerIdx != null) {
      setFillLayerIdx((prev) => remapLayerSelection(prev, fromIdx, toIdx));
    }

    if (typeof layerSelected === "number") {
      const next = remapLayerSelection(layerSelected, fromIdx, toIdx);
      if (next !== layerSelected) setLayerSelected?.(next);
    }
  }, [
    updateSvg, setColors, colors, embroidery,
    layerSelected, setLayerSelected, eraseLayerIdx,
  ]);

  // Delete a layer end-to-end: SVG children, colors, embroidery hook stores
  // (layerStitchSettings, layerRails), local layer-keyed state, AI analysis
  // entries, and the active selection / erase target. Refuses to delete the
  // last remaining layer so the editor always has something to render.
  const handleDeleteLayer = useCallback((deletedIdx) => {
    if (typeof deletedIdx !== "number" || deletedIdx < 0) return;
    if (!Array.isArray(colors) || deletedIdx >= colors.length) return;
    if (colors.length <= 1) return; // never delete the last layer

    // Capture stitch state before the delete so we can push an undo marker
    const preSettings = embroidery?.layerStitchSettings;
    const preRails = embroidery?.layerRails;

    setActiveRailDrawing(null);
    setActiveRungDrawing(null);
    setRailSelection(null);

    if (typeof updateSvg === "function") {
      updateSvg((prev) => {
        if (!prev?.children) return prev;
        if (deletedIdx >= prev.children.length) return prev;
        const nextChildren = [...prev.children];
        nextChildren.splice(deletedIdx, 1);
        return { ...prev, children: nextChildren };
      });
    }

    if (typeof setColors === "function") {
      const next = [...colors];
      next.splice(deletedIdx, 1);
      setColors(next);
    }

    embroidery?.handleLayerDelete?.(deletedIdx);

    // Push a coordinated undo marker so Cmd+Z restores both the SVG layer
    // and the stitch settings in a single step.
    embroidery?.pushSvgLayerDeleteMarker?.(deletedIdx, preSettings, preRails);

    const dropAndShift = (prev) => {
      if (!prev) return prev;
      const next = {};
      for (const [k, val] of Object.entries(prev)) {
        const idx = parseInt(k, 10);
        if (Number.isNaN(idx)) { next[k] = val; continue; }
        if (idx === deletedIdx) continue;
        next[idx > deletedIdx ? idx - 1 : idx] = val;
      }
      return next;
    };
    setLayerRails(dropAndShift);
    setLayerPieces(dropAndShift);

    setAiAnalysis((prev) => {
      if (!prev?.layers || !Array.isArray(prev.layers)) return prev;
      if (deletedIdx >= prev.layers.length) return prev;
      const arr = [...prev.layers];
      arr.splice(deletedIdx, 1);
      return { ...prev, layers: arr };
    });

    if (eraseLayerIdx != null) {
      if (eraseLayerIdx === deletedIdx) {
        setEraseActive(false);
        setEraseLayerIdx(null);
      } else if (eraseLayerIdx > deletedIdx) {
        setEraseLayerIdx(eraseLayerIdx - 1);
      }
    }

    if (pixelEraseLayerIdx != null) {
      if (pixelEraseLayerIdx === deletedIdx) {
        setPixelEraseActive(false);
        setPixelEraseLayerIdx(null);
      } else if (pixelEraseLayerIdx > deletedIdx) {
        setPixelEraseLayerIdx(pixelEraseLayerIdx - 1);
      }
    }

    if (drawLayerIdx != null) {
      if (drawLayerIdx === deletedIdx) {
        setDrawActive(false);
        setDrawLayerIdx(null);
      } else if (drawLayerIdx > deletedIdx) {
        setDrawLayerIdx(drawLayerIdx - 1);
      }
    }

    if (morphLayerIdx != null) {
      if (morphLayerIdx === deletedIdx) {
        setMorphActive(false);
        setMorphLayerIdx(null);
      } else if (morphLayerIdx > deletedIdx) {
        setMorphLayerIdx(morphLayerIdx - 1);
      }
    }

    if (fillLayerIdx != null) {
      if (fillLayerIdx === deletedIdx) {
        setFillActive(false);
        setFillLayerIdx(null);
      } else if (fillLayerIdx > deletedIdx) {
        setFillLayerIdx(fillLayerIdx - 1);
      }
    }

    // Match the SVG editor's selection rules after a delete: stay on the
    // same slot if it now points at a different layer, fall back to the new
    // last layer if the deleted one was at the end, or revert to "all" when
    // only one layer remains.
    const remainingCount = colors.length - 1;
    if (typeof layerSelected === "number") {
      if (remainingCount <= 0) {
        setLayerSelected?.("all");
      } else if (layerSelected === deletedIdx) {
        setLayerSelected?.(Math.min(layerSelected, remainingCount - 1));
      } else if (layerSelected > deletedIdx) {
        setLayerSelected?.(layerSelected - 1);
      }
    }
  }, [
    updateSvg, setColors, colors, embroidery,
    layerSelected, setLayerSelected, eraseLayerIdx,
  ]);

  const handleColorChange = useLayerColorHistory(updateSvg, embroidery?.pushSvgColorMarker);

  const handleDuplicateLayer = useCallback((sourceIdx) => {
    if (typeof sourceIdx !== "number" || sourceIdx < 0) return;
    if (!Array.isArray(colors) || sourceIdx >= colors.length) return;

    const insertIdx = sourceIdx + 1;

    if (typeof updateSvg === "function") {
      updateSvg((prev) => {
        if (!prev?.children) return prev;
        if (sourceIdx >= prev.children.length) return prev;
        const clone = JSON.parse(JSON.stringify(prev.children[sourceIdx]));
        const nextChildren = [...prev.children];
        nextChildren.splice(insertIdx, 0, clone);
        return { ...prev, children: nextChildren };
      });
    }

    if (typeof setColors === "function") {
      const next = [...colors];
      next.splice(insertIdx, 0, colors[sourceIdx]);
      setColors(next);
    }

    embroidery?.handleLayerInsert?.(insertIdx, sourceIdx);

    setLayerSelected?.(insertIdx);
  }, [updateSvg, setColors, colors, embroidery, setLayerSelected]);

  // Create a sublayer: a new layer that shares the source layer's color and
  // group. It clones the source geometry (the user then carves it with
  // Cut/Paint/Delete) and inherits its stitch settings. A sublayer is a
  // first-class layer, so every tool works on it unchanged.
  const handleAddSublayer = useCallback((sourceIdx) => {
    if (typeof sourceIdx !== "number" || sourceIdx < 0) return null;
    if (!Array.isArray(colors) || sourceIdx >= colors.length) return null;

    const children = svgJoined?.children || [];
    if (sourceIdx >= children.length) return null;

    // Resolve (or mint) the group id, then find the last member so the new
    // sublayer slots in directly beneath its siblings.
    const existingGroup = children[sourceIdx]?.attributes?.[SUBLAYER_GROUP_ATTR];
    const groupId = existingGroup || `sub_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
    let lastIdx = sourceIdx;
    children.forEach((c, i) => {
      if (c?.attributes?.[SUBLAYER_GROUP_ATTR] === groupId) lastIdx = Math.max(lastIdx, i);
    });
    const insertIdx = lastIdx + 1;

    if (typeof updateSvg === "function") {
      updateSvg((prev) => {
        if (!prev?.children || sourceIdx >= prev.children.length) return prev;
        const nextChildren = [...prev.children];
        // Tag the source as the group parent (idempotent if already tagged).
        nextChildren[sourceIdx] = {
          ...nextChildren[sourceIdx],
          attributes: { ...nextChildren[sourceIdx].attributes, [SUBLAYER_GROUP_ATTR]: groupId },
        };
        const clone = JSON.parse(JSON.stringify(nextChildren[sourceIdx]));
        clone.attributes = { ...clone.attributes, [SUBLAYER_GROUP_ATTR]: groupId };
        nextChildren.splice(insertIdx, 0, clone);
        return { ...prev, children: nextChildren };
      });
    }

    if (typeof setColors === "function") {
      const next = [...colors];
      next.splice(insertIdx, 0, colors[sourceIdx]);
      setColors(next);
    }

    embroidery?.handleLayerInsert?.(insertIdx, sourceIdx);
    setLayerSelected?.(insertIdx);
    return insertIdx;
  }, [updateSvg, setColors, colors, embroidery, setLayerSelected, svgJoined]);

  const layerMeta = useMemo(() => computeLayerMeta(svgJoined?.children), [svgJoined]);

  // Hoop state lives in the embroidery hook so it's included in serialize/hydrate
  const selectedHoop = embroidery?.selectedHoop ?? 0;
  const setSelectedHoop = embroidery?.setSelectedHoop ?? (() => {});
  const targetWidth = embroidery?.targetWidth ?? 100;
  const setTargetWidth = embroidery?.setTargetWidth ?? (() => {});
  const targetHeight = embroidery?.targetHeight ?? 100;
  const setTargetHeight = embroidery?.setTargetHeight ?? (() => {});
  const hoop = HOOP_SIZES[selectedHoop];
  const hoopWidth = hoop?.width || targetWidth || 100;
  const hoopHeight = hoop?.height || targetHeight || 100;
  const [lockAspectRatio, setLockAspectRatio] = useState(true);

  const [selectedFabric, setSelectedFabric] = useState("cotton");
  const [stitchSettings, setStitchSettings] = useState({
    stitch_type: "auto_fill",
    fill_method: "auto_fill",
    angle: null,
    row_spacing_mm: 0.2,
    max_stitch_length_mm: 2.8,
    staggers: 4,
    expand_mm: 0.16,
    fill_underlay: true,
    underlay_type: "auto",
    fill_underlay_row_spacing_mm: 2.0,
    fill_underlay_inset_mm: 0.3,
    fill_underlay_max_stitch_length_mm: 6.0,
    fill_underlay_angle: null,
    pull_compensation_mm: 0.25,
    zigzag_spacing_mm: 0.4,
    running_stitch_length_mm: 2.0,
    bean_stitch_repeats: 1,
    randomize_stitch_length: true,
    random_stitch_length_jitter_percent: 25,
    optimize_order: true,
    satin_width_mm: 2.0,
    contour_underlay_inset_mm: 0.4,
    zigzag_underlay_spacing_mm: 3.0,
    satin_underlay_profile: "auto",
    push_compensation_mm: 0,
    split_method: "staggered",
    split_staggers: 4,
    short_stitch_distance_mm: 0.25,
    short_stitch_inset: 15,
    reverse_rails: "automatic",
    swap_satin_rails: false,
    ...FILL_DEFAULTS,
  });

  const [options] = useState({
    collapse_len: 3.0,
    min_stitch_len: 0.1,
    disable_ties: false,
    no_trims: false,
    skip_simplify: true,
  });

  const originalDimensions = useMemo(() => {
    if (!svgJoined?.attributes?.viewBox) return { width: 100, height: 100, aspectRatio: 1 };
    const vb = svgJoined.attributes.viewBox.split(" ");
    const w = parseFloat(vb[2]) || 100;
    const h = parseFloat(vb[3]) || 100;
    return { width: w, height: h, aspectRatio: w / h };
  }, [svgJoined]);

  // Fit design to default hoop on first load — skipped when Firestore has saved hoop settings
  useEffect(() => {
    if (savedStitchState?.hoopSettings) return; // already restored from Firestore
    // Fit into the 4×4 hoop's *usable* field, not its nominal edge, so the
    // generated PES/DST clears the machine's hoop limit (see HOOP_SAFETY_MARGIN_MM).
    const hoop = { width: safeHoopDim(100), height: safeHoopDim(100) };
    const ar = originalDimensions.aspectRatio;
    const hoopAspect = hoop.width / hoop.height;
    if (ar > hoopAspect) {
      setTargetWidth(hoop.width);
      setTargetHeight(Math.round(hoop.width / ar));
    } else {
      setTargetHeight(hoop.height);
      setTargetWidth(Math.round(hoop.height * ar));
    }
  }, [originalDimensions, savedStitchState?.hoopSettings, setTargetWidth, setTargetHeight]);

  // AI stitch analysis: cached per file on the parent ref so tab switches do not remount
  // into a fresh analysis + updatePassSettings overwrite. Defaults apply only when there is
  // no stitch data yet (in memory or embedded in the SVG).
  useEffect(() => {
    if (!svgJoined) return;

    // In-memory cache hit (e.g. tab switch without full remount) — use it immediately.
    if (fileId && stitchAiAnalysisCacheRef?.current?.fileId === fileId && stitchAiAnalysisCacheRef.current.analysis) {
      setAiAnalysis(stitchAiAnalysisCacheRef.current.analysis);
      setAiLoading(false);
      setAiError(null);
      return;
    }

    // Wait for the Firestore load to settle before deciding whether to call the API.
    // This prevents re-running analysis on every open when a saved result exists.
    if (!stitchStateLoaded) return;

    // Already persisted to Firestore — use it directly, no API call needed.
    if (savedStitchState?.aiAnalysis) {
      setAiAnalysis(savedStitchState.aiAnalysis);
      setAiLoading(false);
      setAiError(null);
      if (fileId && stitchAiAnalysisCacheRef) {
        stitchAiAnalysisCacheRef.current = { fileId, analysis: savedStitchState.aiAnalysis };
      }
      return;
    }

    // File already has stitch configuration — skip re-analysis on every open.
    const hasSavedSettings =
      !!(savedStitchState?.layerStitchSettings && Object.keys(savedStitchState.layerStitchSettings).length > 0) ||
      !!(savedStitchState?.layerRails && Object.keys(savedStitchState.layerRails).length > 0) ||
      !!(savedStitchState?.layerCutMarks && Object.keys(savedStitchState.layerCutMarks).length > 0);

    if (hasSavedSettings) {
      setAiLoading(false);
      setAiError(null);
      return;
    }

    // Analysis passed from the converter before Firestore finishes writing.
    if (initialAiAnalysis && !savedStitchState?.aiAnalysis) {
      setAiAnalysis(initialAiAnalysis);
      setAiLoading(false);
      setAiError(null);
      if (fileId && stitchAiAnalysisCacheRef) {
        stitchAiAnalysisCacheRef.current = { fileId, analysis: initialAiAnalysis };
      }
      onAiAnalysisReady?.(initialAiAnalysis);
      return;
    }

    // New layers use the existing fill defaults without an AI request.
  }, [svgJoined, fileId, colors?.length, colors, stitchStateLoaded, savedStitchState?.aiAnalysis, initialAiAnalysis, onAiAnalysisReady, readOnly]);

  // The stitch tools edit one layer at a time. When the quick layer strip is
  // set to "All", keep the preview visible but cancel layer-specific edit
  // modes so actions cannot silently fall back to layer 1.
  useEffect(() => {
    if (typeof layerSelected === "number") return;
    let cancelled = false;
    (async () => {
      const ok = await leaveCutTool();
      if (cancelled || !ok) return;
      setActiveRailDrawing(null);
      setActiveRungDrawing(null);
      setRailSelection(null);
      setRailEditing(null);
      embroidery?.cancelSplitMode?.();
      embroidery?.cancelCutMode?.();
    })();
    return () => { cancelled = true; };
  }, [layerSelected, leaveCutTool]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync sidebar/toolbar to the selected layer's active pass settings
  useEffect(() => {
    if (!embroidery?.getLayerSettings) return;
    if (typeof layerSelected !== "number") return;
    const idx = layerSelected;
    const ls = embroidery.getLayerSettings(idx);
    let activePass = ls?.passes?.[ls?.activePassIndex || 0] || ls?.passes?.[0];
    if (activePass && isFillPass(activePass)) activePass = resolveFillSettings(activePass);
    if (!activePass) {
      // Layer not yet explicitly configured — reset to auto_fill so stitch type
      // from the previously selected layer doesn't bleed into this one.
      setStitchSettings((prev) => ({ ...prev, ...FILL_DEFAULTS, stitch_type: "auto_fill", fill_method: "auto_fill" }));
      return;
    }
    setStitchSettings((prev) => ({
      ...prev,
      ...(isFillPass(activePass) ? resolveFillSettings(activePass) : {}),
      stitch_type: activePass.stitchType || prev.stitch_type,
      fill_method: activePass.fill_method || activePass.stitchType || prev.fill_method,
      angle: activePass.angle !== undefined ? activePass.angle : prev.angle,
      row_spacing_mm: activePass.row_spacing_mm ?? prev.row_spacing_mm,
      max_stitch_length_mm: activePass.max_stitch_length_mm ?? (activePass.stitchType === "satin" ? 7.0 : prev.max_stitch_length_mm),
      staggers: activePass.staggers ?? prev.staggers,
      expand_mm: activePass.expand_mm ?? prev.expand_mm,
      fill_underlay: activePass.fill_underlay ?? prev.fill_underlay,
      pull_compensation_mm: activePass.pull_compensation_mm ?? prev.pull_compensation_mm,
      push_compensation_mm: activePass.push_compensation_mm ?? prev.push_compensation_mm,
      zigzag_spacing_mm: activePass.zigzag_spacing_mm ?? prev.zigzag_spacing_mm,
      split_method: activePass.split_method ?? prev.split_method,
      split_staggers: activePass.split_staggers ?? prev.split_staggers,
      short_stitch_distance_mm: activePass.short_stitch_distance_mm ?? prev.short_stitch_distance_mm,
      short_stitch_inset: activePass.short_stitch_inset ?? prev.short_stitch_inset,
      satin_underlay_profile: activePass.satin_underlay_profile ?? prev.satin_underlay_profile,
      contour_underlay_inset_mm: activePass.contour_underlay_inset_mm ?? prev.contour_underlay_inset_mm,
      zigzag_underlay_spacing_mm: activePass.zigzag_underlay_spacing_mm ?? prev.zigzag_underlay_spacing_mm,
      reverse_rails: activePass.reverse_rails ?? prev.reverse_rails,
      swap_satin_rails: activePass.swap_satin_rails ?? prev.swap_satin_rails,
      running_stitch_length_mm: activePass.running_stitch_length_mm ?? prev.running_stitch_length_mm,
      bean_stitch_repeats: activePass.bean_stitch_repeats ?? prev.bean_stitch_repeats,
      randomize_stitch_length: activePass.randomize_stitch_length ?? prev.randomize_stitch_length,
    }));
  }, [layerSelected]); // eslint-disable-line react-hooks/exhaustive-deps

  // Does the design contain any satin layer? If so, fills get a small default
  // underlap so they tuck under the satin border and don't leave a gap of bare
  // fabric where the colors meet (a satin column can't expand to meet them).
  const designHasSatin = useMemo(() => {
    const railsHasSatin = (entry) => {
      const pairs = entry?.pairs || [];
      if (pairs.some((p) => p.rail0 && p.rail1)) return true;
      return !!(entry?.rail0 && entry?.rail1);
    };
    if (Object.values(layerRails || {}).some(railsHasSatin)) return true;
    if (Object.values(embroidery?.layerStitchSettings || {}).some((ls) => {
      const passes = ls?.passes || (ls ? [ls] : []);
      return passes.some((p) => (p?.stitchType || p?.fill_method) === "satin");
    })) return true;
    return false;
  }, [layerRails, embroidery?.layerStitchSettings, aiAnalysis]);

  const injectInkstitchAttributes = useCallback((element, settings, layerIndex) => {
    if (!element) return element;
    if (!element.attributes) element.attributes = {};
    const embroiderable = ["path", "rect", "circle", "ellipse", "polygon", "polyline", "line"];

    // Check if this specific layer has manual satin rails drawn
    const railEntry = layerRails[layerIndex];
    const pairs = railEntry?.pairs || [];
    let completePairs = pairs.filter((p) => p.rail0 && p.rail1);
    if (completePairs.length === 0 && railEntry?.rail0 && railEntry?.rail1) {
      completePairs = [{ rail0: railEntry.rail0, rail1: railEntry.rail1 }];
    }
    const layerHasRails = completePairs.length > 0;

    // mm per SVG user unit, for measuring satin column widths. Rails are stored
    // in the original viewBox space; the design is scaled to targetWidth/Height.
    const vbW = originalDimensions?.width || 100;
    const vbH = originalDimensions?.height || 100;
    const mmPerUnit = (((targetWidth || vbW) / vbW) + ((targetHeight || vbH) / vbH)) / 2;

    // Read user's per-layer override (set via toolbar). Highest priority below rails.
    const layerSettings = embroidery?.layerStitchSettings?.[layerIndex];
    const userPass = layerSettings?.passes?.[layerSettings?.activePassIndex || 0];
    const userStitchType = userPass?.stitchType || userPass?.fill_method;

    const aiLayer = !layerHasRails && aiAnalysis?.layers?.[layerIndex];
    const aiStitchType = aiLayer ? sanitizeAiStitchType(aiLayer.recommended_stitch_type) : null;

    // Resolve the effective stitch type: user override > AI recommendation > global settings.
    const resolvedStitchType = userStitchType || aiStitchType || settings.stitch_type;
    const resolvedFillMethod = userPass?.fill_method || userStitchType || aiStitchType || settings.fill_method;

    const effective = (userPass || aiLayer)
      ? {
          ...settings,
          ...(userPass || {}),
          stitch_type: resolvedStitchType,
          fill_method: resolvedFillMethod,
          angle: userPass?.angle ?? aiLayer?.angle ?? settings.angle,
          row_spacing_mm: userPass?.row_spacing_mm ?? aiLayer?.row_spacing_mm ?? settings.row_spacing_mm,
          fill_underlay: userPass?.fill_underlay ?? aiLayer?.fill_underlay ?? settings.fill_underlay,
          pull_compensation_mm: userPass?.pull_compensation_mm ?? aiLayer?.pull_compensation_mm ?? settings.pull_compensation_mm,
          running_stitch_length_mm: userPass?.running_stitch_length_mm ?? settings.running_stitch_length_mm,
          max_stitch_length_mm: userPass?.max_stitch_length_mm ?? aiLayer?.max_stitch_length_mm,
          expand_mm: userPass?.expand_mm ?? aiLayer?.expand_mm,
          staggers: userPass?.staggers ?? aiLayer?.staggers,
        }
      : settings;

    // Stitch type is authoritative: only treat the layer as satin if the user
    // explicitly chose satin, or it has rails AND wasn't explicitly set to a
    // fill/running type. This stops a Fill sublayer that inherited a satin
    // parent's rails from being exported as a duplicate satin outline (which
    // dropped the layer's actual fill from the generated file).
    const userWantsNonSatin = !!userStitchType && userStitchType !== "satin";
    const isSatin =
      effective.stitch_type === "satin" ||
      effective.fill_method === "satin" ||
      (layerHasRails && !userWantsNonSatin);

    const buildSatinAttrs = (widthMm = null) => {
      const attrs = {};
      attrs["inkstitch:satin_column"] = "true";
      // Preserve the spacing shown in the layer controls at every column width.
      attrs["inkstitch:zigzag_spacing_mm"] = String(effective.zigzag_spacing_mm ?? 0.4);
      const satinPull = effective.pull_compensation_mm != null ? effective.pull_compensation_mm : SATIN_PULL_COMP_MM;
      attrs["inkstitch:pull_compensation_mm"] = String(satinPull);
      if (effective.push_compensation_mm != null) attrs["inkstitch:push_compensation_mm"] = String(effective.push_compensation_mm);
      if (effective.max_stitch_length_mm) attrs["inkstitch:max_stitch_length_mm"] = String(effective.max_stitch_length_mm);
      if (effective.split_method) attrs["inkstitch:split_method"] = effective.split_method;
      if (effective.split_method === "staggered" && effective.split_staggers) {
        attrs["inkstitch:split_staggers"] = String(effective.split_staggers);
      }
      if (effective.reverse_rails) attrs["inkstitch:reverse_rails"] = effective.reverse_rails;
      attrs["inkstitch:swap_satin_rails"] = effective.swap_satin_rails ? "true" : "false";
      Object.assign(attrs, satinUnderlayAttributes(effective, widthMm));
      // Short stitch for smooth edges on curves
      if (effective.short_stitch_distance_mm != null) {
        attrs["inkstitch:short_stitch_distance_mm"] = String(effective.short_stitch_distance_mm);
        attrs["inkstitch:short_stitch_inset"] = String(effective.short_stitch_inset ?? 15);
      }
      return attrs;
    };

    const findColor = (el) => {
      if (el.attributes?.fill && el.attributes.fill !== "none") return el.attributes.fill;
      if (el.attributes?.stroke && el.attributes.stroke !== "none") return el.attributes.stroke;
      if (el.children) {
        for (const c of el.children) { const col = findColor(c); if (col) return col; }
      }
      return null;
    };


    if (isSatin && layerHasRails) {
      const threadColor = findColor(element) || "#000000";

      const buildPairD = (pair) => {
        const rungPaths = (pair.rungs || []).join(" ");
        return pair.rail0 + " " + pair.rail1 + (rungPaths ? " " + rungPaths : "");
      };

      if (completePairs.length === 1) {
        const widthMm = measureSatinColumnWidthMm(completePairs[0].rail0, completePairs[0].rail1, mmPerUnit);
        const satinAttrs = buildSatinAttrs(widthMm);
        element.name = "path";
        element.attributes.d = buildPairD(completePairs[0]);
        element.attributes.stroke = threadColor;
        element.attributes["stroke-width"] = "1";
        element.attributes.fill = "none";
        Object.assign(element.attributes, satinAttrs);
        element.children = [];
      } else {
        element.name = "g";
        // Each column can have its own width, so build per-pair attrs.
        element.children = completePairs.map((pair) => {
          const widthMm = measureSatinColumnWidthMm(pair.rail0, pair.rail1, mmPerUnit);
          return {
            name: "path",
            type: "element",
            attributes: {
              d: buildPairD(pair),
              stroke: threadColor,
              "stroke-width": "1",
              fill: "none",
              ...buildSatinAttrs(widthMm),
            },
            children: [],
          };
        });
        element.attributes.fill = "none";
      }

      cleanSatinElement(element);
      return element;
    }

    if (isSatin && !layerHasRails && embroiderable.includes(element.name)) {
      const threadColor = findColor(element) || "#000000";

      const layerEl = svgSplit?.children?.[layerIndex];
      const splitChildren = layerEl?.children;
      let d0, d1;
      if (splitChildren?.length === 2) {
        d0 = splitChildren[0].attributes?.d;
        d1 = splitChildren[1].attributes?.d;
      } else if (!splitChildren?.length) {
        const compoundD = layerEl?.attributes?.d || element.attributes?.d;
        if (compoundD) {
          const subpaths = compoundD.split(/(?=M)/i).filter((s) => s.trim());
          if (subpaths.length === 2) {
            d0 = subpaths[0];
            d1 = subpaths[1];
          }
        }
      }
      if (d0 && d1) {
        const widthMm = measureSatinColumnWidthMm(d0, d1, mmPerUnit);
        const satinAttrs = buildSatinAttrs(widthMm);
        element.name = "path";
        element.attributes.d = d0 + " " + d1;
        element.attributes.stroke = threadColor;
        element.attributes["stroke-width"] = "1";
        element.attributes.fill = "none";
        Object.assign(element.attributes, satinAttrs);
        element.children = [];
        cleanSatinElement(element);
        return element;
      }

      element.attributes["inkstitch:satin_column"] = "true";
      element.attributes.stroke = threadColor;
      element.attributes["stroke-width"] = String((effective.satin_width_mm || 2.0) * 3.7795);
      element.attributes.fill = "none";
      if (element.attributes.d) {
        element.attributes.d = element.attributes.d.replace(/\s*Z\s*$/i, "");
      }
      Object.assign(element.attributes, buildSatinAttrs(effective.satin_width_mm || null));
      cleanSatinElement(element);
      if (element.children && Array.isArray(element.children)) {
        element.children.forEach((c) => {
          if (c.attributes?.d) c.attributes.d = c.attributes.d.replace(/\s*Z\s*$/i, "");
        });
      }
      return element;
    }

    const isRunning = effective.stitch_type === "running" || effective.fill_method === "running";

    if (isRunning && embroiderable.includes(element.name)) {
      runningStitchAttributes(element.attributes);
      element.attributes["inkstitch:running_stitch_length_mm"] = String(effective.running_stitch_length_mm || 2.0);
      // Professional: bean stitch (triple) for bolder, more visible outlines
      element.attributes["inkstitch:bean_stitch_repeats"] = String(effective.bean_stitch_repeats ?? 1);
      element.attributes["inkstitch:running_stitch_tolerance_mm"] = "0.3";
    } else if (embroiderable.includes(element.name)) {
      const method = effective.fill_method || STITCH_TYPES_MAP[effective.stitch_type] || effective.stitch_type || "legacy_fill";
      const safeFillMethod = VALID_FILL_METHODS.has(method) ? method : "legacy_fill";
      element.attributes["inkstitch:fill_method"] = safeFillMethod;
      if (effective.angle != null) element.attributes["inkstitch:angle"] = String(effective.angle);
      if (effective.row_spacing_mm) element.attributes["inkstitch:row_spacing_mm"] = String(effective.row_spacing_mm);
      element.attributes["inkstitch:fill_underlay"] = effective.fill_underlay ? "true" : "false";
      if (effective.fill_underlay) {
        if (effective.fill_underlay_row_spacing_mm) element.attributes["inkstitch:fill_underlay_row_spacing_mm"] = String(effective.fill_underlay_row_spacing_mm);
        if (effective.fill_underlay_angle != null) element.attributes["inkstitch:fill_underlay_angle"] = String(effective.fill_underlay_angle);
        // Professional: inset underlay from edges to prevent showing through
        element.attributes["inkstitch:fill_underlay_inset_mm"] = String(effective.fill_underlay_inset_mm || 0.3);
        element.attributes["inkstitch:fill_underlay_max_stitch_length_mm"] = String(effective.fill_underlay_max_stitch_length_mm || 6.0);
      }
      if (effective.pull_compensation_mm) element.attributes["inkstitch:pull_compensation_mm"] = String(effective.pull_compensation_mm);
      if (effective.zigzag_spacing_mm) element.attributes["inkstitch:zigzag_spacing_mm"] = String(effective.zigzag_spacing_mm);
      if (effective.max_stitch_length_mm) element.attributes["inkstitch:max_stitch_length_mm"] = String(effective.max_stitch_length_mm);
      const expandMm = effective.expand_mm != null
        ? effective.expand_mm
        : (designHasSatin ? SATIN_UNDERLAP_MM : null);
      if (expandMm) element.attributes["inkstitch:expand_mm"] = String(expandMm);
      if (effective.staggers) element.attributes["inkstitch:staggers"] = String(effective.staggers);
      if (effective.running_stitch_length_mm) element.attributes["inkstitch:running_stitch_length_mm"] = String(effective.running_stitch_length_mm);
      // Professional: randomize stitch lengths to prevent Moiré/valley artifacts
      element.attributes["inkstitch:enable_random_stitch_length"] = effective.randomize_stitch_length !== false ? "true" : "false";
      if (effective.randomize_stitch_length !== false) {
        element.attributes["inkstitch:random_stitch_length_jitter_percent"] = String(effective.random_stitch_length_jitter_percent || 25);
      }
    }
    if (element.children && Array.isArray(element.children)) {
      element.children = element.children.map((c) => injectInkstitchAttributes(c, settings, layerIndex));
    }
    return element;
  }, [aiAnalysis, layerRails, svgSplit, embroidery?.layerStitchSettings, designHasSatin, targetWidth, targetHeight, originalDimensions]);

  const scaleSvgToSize = useCallback((svgJson, tw, th) => {
    const scaled = JSON.parse(JSON.stringify(svgJson));
    scaled.attributes.width = `${tw}mm`;
    scaled.attributes.height = `${th}mm`;
    scaled.attributes.preserveAspectRatio = "none";
    scaled.attributes["xmlns:inkstitch"] = "http://inkstitch.org/namespace";

    const hasPerLayer = embroidery?.layerStitchSettings && Object.keys(embroidery.layerStitchSettings).length > 0;

    if (aiAnalysis?.layers && scaled.children?.length > 0) {
      scaled.children = scaled.children.map((child, i) =>
        injectInkstitchAttributes(child, stitchSettings, i)
      );
      return scaled;
    }

    if (hasPerLayer) {
      const normalizeRails = (rails) => {
        const out = {};
        for (const [k, v] of Object.entries(rails || {})) {
          if (v?.pairs) { out[k] = v; }
          else if (v?.rail0 || v?.rail1) { out[k] = { pairs: [{ rail0: v.rail0, rail1: v.rail1 }] }; }
        }
        return out;
      };
      const mergedRails = { ...normalizeRails(embroidery.layerRails), ...normalizeRails(layerRails) };
      return injectEmbroideryAttributes(scaled, embroidery.layerStitchSettings, mergedRails);
    }

    scaled.children = (scaled.children || []).map((child, i) =>
      injectInkstitchAttributes(child, stitchSettings, i)
    );
    return scaled;
  }, [embroidery, stitchSettings, injectInkstitchAttributes, aiAnalysis, layerRails]);

  const doConvertRequest = useCallback(async (svgContent, fmt, signal) => {
    const response = await fetch(`${getEmbroideryServiceUrl()}/convert`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      signal,
        body: JSON.stringify({
        svgContent,
        format: fmt,
        // Forward the selected hoop so the converter can stamp the correct PES
        // hoop metadata (0 = 100×100 / 4×4, else 130×180). Without this every PES
        // pyembroidery writes declares itself a 5×7 design and 4×4 machines
        // (e.g. Brother SE700) silently hide the file. DST has no hoop tag so it
        // is unaffected — which is why DST shows but PES doesn't.
        options: { ...options, hoop_width_mm: hoopWidth, hoop_height_mm: hoopHeight },
      }),
    });
    if (!response.ok) {
      let serverMsg = "";
      try {
        const errBody = await response.json();
        serverMsg = errBody?.error || "";
      } catch (_) { /* couldn't parse error body */ }
      throw new Error(serverMsg || `Server error (${response.status})`);
    }
    const result = await response.json();
    if (!result.success) throw new Error(result.error || "Conversion failed");
    return result;
  }, [options, hoopWidth, hoopHeight]);

  const cancelConversion = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    onConversionEnd?.();
    setIsConverting(false);
    setConvertStatus("");
    message.destroy("retry");
    message.info("Conversion cancelled.");
  }, [onConversionEnd]);

  // Auto-restart a conversion that was interrupted by a page refresh.
  // Two guards prevent the "stuck for hours, refreshes don't help" loop:
  //   1. `autoRestartedRef` — fires at most once per mount.
  //   2. `sessionStorage[autoRestarted:<fileId>]` — fires at most once per
  //      browser-tab session for a given file. Cleared in `handleConversionEnd`
  //      when the user explicitly cancels or the conversion completes.
  useEffect(() => {
    if (!pendingConversion?.format) return;
    if (!stitchStateLoaded) return;
    if (autoRestartedRef && autoRestartedRef.current) return;
    if (fileId) {
      try {
        if (sessionStorage.getItem(`autoRestarted:${fileId}`) === "1") return;
        sessionStorage.setItem(`autoRestarted:${fileId}`, "1");
      } catch (_) { /* sessionStorage unavailable — fall back to per-mount guard only */ }
    }
    if (autoRestartedRef) autoRestartedRef.current = true;
    // Small delay so the editor fully mounts before triggering the network call.
    const t = setTimeout(() => handleConvert(pendingConversion.format), 1500);
    return () => clearTimeout(t);
  }, [pendingConversion, stitchStateLoaded, fileId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Warn the user before closing/refreshing while a conversion is in flight.
  useEffect(() => {
    if (!isConverting) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "Embroidery file is still generating — if you leave now you'll need to regenerate it.";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isConverting]);

  const handleConvert = useCallback(async (format) => {
    if (!svgJoined) return;

    const emptyLayers = emptySatinLayers(
      svgJoined, embroidery?.layerStitchSettings,
      {...embroidery?.layerRails, ...layerRails}, !!aiAnalysis?.layers
    );
    if (emptyLayers.length) {
      const proceed = await new Promise(resolve => {
        Modal.confirm({
          title: emptyLayers.length === 1 ? "This Satin layer has no columns" : "Some Satin layers have no columns",
          content: <>
            <p>{emptyLayers.map(layer => layer.label).join(", ")} {emptyLayers.length === 1 ? "has" : "have"} no complete pair of satin rails.</p>
            <p>{aiAnalysis?.layers
              ? "Export will try to create satin from the original paths or use a satin outline. This may not match the satin columns you intended."
              : "Export keeps the original artwork: filled shapes will stitch as Fill, and existing strokes may be treated as satin outlines. No drawn satin columns will be used."}</p>
            <p>Go back and add satin columns, or continue with this export.</p>
          </>,
          okText: "Continue export", cancelText: "Back to editor", autoFocusButton: "cancel",
          onOk: () => resolve(true), onCancel: () => { setExportModalOpen(false); resolve(false); },
        });
      });
      if (!proceed) return;
    }


    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }

    const fmt = format || selectedFormat;
    const controller = new AbortController();
    abortRef.current = controller;

    onConversionStart?.(fmt);
    setIsConverting(true);
    setError(null);
    setConvertStatus("Generating stitch preview...");
    setConversionResult(null);
    setRealStitchData(null);
    setStitchFileSaveStatus("idle");
    setSavedFileMeta(null);

    const scaled = scaleSvgToSize(svgJoined, targetWidth, targetHeight);
    // Non-destructive fabric compensation: nudges density / pull-comp / underlay
    // on top of the resolved per-layer settings (cotton = neutral baseline).
    applyFabricCompensation(scaled, selectedFabric);
    const svgContent = stringify(scaled);

    const MAX_RETRIES = 2;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      if (controller.signal.aborted) {
        setIsConverting(false);
        setConvertStatus("");
        return;
      }

      const timeoutMs = attempt === 0 ? 300_000 : 360_000;
      const timeout = setTimeout(() => controller.abort(), timeoutMs);

      try {
        if (attempt > 0) {
          setError(null);
          setConvertStatus("Server warming up — retrying...");
        }

        const result = await doConvertRequest(svgContent, fmt, controller.signal);
        clearTimeout(timeout);

        if (abortRef.current !== controller) return;
        abortRef.current = null;

        onConversionEnd?.();
        setConversionResult(result);
        const hoopFit = result?.metadata?.hoop_fit;
        if (hoopFit && hoopFit.fits === false) {
          message.warning({
            content: `Converted, but the design (${hoopFit.designMm?.width}×${hoopFit.designMm?.height}mm) is larger than your ${hoopFit.hoopMm?.width}×${hoopFit.hoopMm?.height}mm hoop — most machines will hide it. Shrink it to fit before stitching.`,
            duration: 8,
          });
        } else {
          message.success(`Converted to ${fmt.toUpperCase()} successfully!`);
        }
        setIsConverting(false);
        setConvertStatus("");

        if (result.data) {
          setTimeout(() => {
            try {
              const parsed = parseEmbroideryFile(result.data, fmt);
              if (parsed) setRealStitchData(parsed);
            } catch (parseErr) {
              console.warn("[StitchEditor] Stitch parse failed:", parseErr);
            }
          }, 0);
        }
        return;
      } catch (err) {
        clearTimeout(timeout);

        if (abortRef.current !== controller) return;

        const isTimeout = err.name === "AbortError";

        if (isTimeout && attempt < MAX_RETRIES - 1) {
          continue;
        }

        abortRef.current = null;

        const msg = isTimeout
          ? "Conversion timed out — please try again or simplify the design."
          : (err.message || "Failed to convert.");
        onConversionEnd?.();
        setError(msg);
        message.error(msg);
        setIsConverting(false);
        setConvertStatus("");
        return;
      }
    }
  }, [svgJoined, embroidery?.layerStitchSettings, embroidery?.layerRails, layerRails, aiAnalysis?.layers, selectedFormat, targetWidth, targetHeight, selectedFabric, scaleSvgToSize, doConvertRequest, onConversionStart, onConversionEnd]);


  const handleDownload = useCallback((overrideName) => {
    if (!conversionResult?.data) return;
    // Sanitize the user-supplied name: strip any extension, illegal path/file
    // characters and surrounding whitespace, and fall back to the design's
    // name (or a generic default) when it ends up empty.
    const sanitize = (name) =>
      String(name || "")
        .trim()
        .replace(/\.[^/.]+$/, "")
        .replace(/[\\/:*?"<>|]+/g, "")
        .trim();
    const baseName = sanitize(overrideName) || sanitize(fileName) || "embroidery";
    const binaryString = atob(conversionResult.data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
    const blob = new Blob([bytes], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${baseName}.${selectedFormat}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    message.success(`Downloaded ${baseName}.${selectedFormat}`);
    trackFileConversion(selectedFormat);
    // Fires after the file is already on disk, so anything the host does with
    // this (e.g. the share prompt) can never hold up a download.
    onDownloaded?.();
  }, [conversionResult, fileName, selectedFormat, onDownloaded]);

  // Format is picked on the Size step. Switching it invalidates any file we
  // already generated (those bytes are in the old format), so we drop the
  // result and reset the save state — the user regenerates in the new format.
  // Internal callers (hydrate / initial-DST effects) use the raw setter so they
  // can set the format and the matching result together without clearing it.
  const handleFormatChange = useCallback((fmt) => {
    if (fmt === selectedFormat) return;
    setSelectedFormat(fmt);
    setConversionResult(null);
    setRealStitchData(null);
    setStitchFileSaveStatus("idle");
    setSavedFileMeta(null);
  }, [selectedFormat]);

  // Auto-save the latest generated stitch file to the user's account. Triggers
  // whenever `conversionResult` changes (post-convert) — or on hydrate if the
  // result came back from Storage we keep the saved indicator without
  // re-uploading the same bytes. We track an upload key so a parent re-render
  // never causes a duplicate upload.
  useEffect(() => {
    if (!onStitchFileGenerated) return;
    const data = conversionResult?.data;
    if (!data) return;
    // If this result was hydrated from storage (no need to re-upload).
    if (conversionResult?.fromCloud && conversionResult?.savedFile) {
      lastUploadKeyRef.current = `${conversionResult.savedFile.format}:${data.length}`;
      setSavedFileMeta(conversionResult.savedFile);
      setStitchFileSaveStatus("saved");
      return;
    }
    const key = `${selectedFormat}:${data.length}`;
    if (lastUploadKeyRef.current === key) return;
    lastUploadKeyRef.current = key;

    let cancelled = false;
    setStitchFileSaveStatus("saving");
    onStitchFileGenerated({
      format: selectedFormat,
      base64: data,
      metadata: conversionResult?.metadata || null,
    })
      .then((saved) => {
        if (cancelled) return;
        if (saved) {
          setSavedFileMeta(saved);
          setStitchFileSaveStatus("saved");
        } else {
          setStitchFileSaveStatus("error");
        }
      })
      .catch(() => {
        if (cancelled) return;
        setStitchFileSaveStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [conversionResult, selectedFormat, onStitchFileGenerated]);

  // Hydrate the editor with the user's most recently generated stitch file
  // when one exists on the loaded stitch state. We pull the bytes from
  // Storage so the preview canvas, machine-setup recommendations, and Download
  // button all light up exactly as if the user had just hit Generate — minus
  // the multi-minute Cloud Run round-trip.
  const hydratedFileIdRef = useRef(null);
  useEffect(() => {
    if (!stitchStateLoaded) return;
    if (!fileId) return;
    const last = savedStitchState?.lastGeneratedFile;
    if (!last?.downloadURL || !last?.format) {
      hydratedFileIdRef.current = fileId;
      return;
    }
    if (hydratedFileIdRef.current === fileId) return;
    hydratedFileIdRef.current = fileId;
    // Don't clobber an in-progress conversion or a freshly-generated result.
    if (isConverting || conversionResult?.data) return;

    let cancelled = false;
    (async () => {
      const base64 = await loadStitchFileFromStorage(last.downloadURL);
      if (cancelled || !base64) return;
      // Preset the format selector to whatever was last generated so the
      // preview + Download button use the right extension.
      setSelectedFormat(last.format);
      setConversionResult({
        success: true,
        data: base64,
        metadata: last.metadata || null,
        // Markers used by the auto-save effect to skip re-uploading these
        // bytes (they came from Storage to begin with) and by the UI to show
        // the "saved" indicator immediately.
        fromCloud: true,
        savedFile: last,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [stitchStateLoaded, fileId, savedStitchState?.lastGeneratedFile, isConverting, conversionResult?.data]);

  // If the converter pre-generated a DST and passed it directly (bypassing the
  // async Firestore write), seed conversionResult immediately so the export modal
  // shows the file without requiring a fresh conversion.
  const initialDstUsedRef = useRef(false);
  useEffect(() => {
    if (!initialDstBase64 || initialDstUsedRef.current) return;
    if (conversionResult?.data || isConverting) return;
    initialDstUsedRef.current = true;
    setSelectedFormat("dst");
    setConversionResult({
      success: true,
      data: initialDstBase64,
      metadata: null,
      fromCloud: true,
      savedFile: null,
    });
  }, [initialDstBase64, conversionResult?.data, isConverting]); // eslint-disable-line react-hooks/exhaustive-deps

  // Quick-convert card removed — users land directly in the stitch editor.

  useEffect(() => {
    if (window.tutorialTriggerAction) {
      window.tutorialTriggerAction('editor-opened', {
        component: 'StitchEditor',
        timestamp: Date.now(),
      });
    }
  }, []);

  const hasEditableLayer = typeof layerSelected === "number";
  const currentLayerIdx = hasEditableLayer ? layerSelected : 0;
  const currentLayerIsSatin = (() => {
    const ls = embroidery?.getLayerSettings?.(currentLayerIdx);
    const activePass = ls?.passes?.[ls?.activePassIndex || 0];
    // Only use embroidery hook data — never fall back to shared stitchSettings,
    // because stitchSettings may still hold the previous layer's stitch type
    // when switching to a layer that has not been explicitly configured yet.
    return activePass?.stitchType === "satin";
  })();
  const autoSatinPath = !hasEditableLayer || svgJoined?.children?.[currentLayerIdx]?.attributes?.transform
    ? null : svgJoined?.children?.[currentLayerIdx]?.attributes?.d;
  const validAutoPreview = autoSatinPreview && currentLayerIsSatin && autoSatinPreview.layer === currentLayerIdx && autoSatinPreview.path === autoSatinPath && autoSatinPreview.revision === layerRails;
  const previewRails = validAutoPreview ? { ...layerRails, [currentLayerIdx]: { pairs: autoSatinPreview.result.pairs, autoCuts: autoSatinPreview.result.cuts } } : layerRails;

  const currentEntry = hasEditableLayer ? layerRails[currentLayerIdx] : null;
  const currentRailPairs = hasEditableLayer ? (currentEntry?.pairs || []) : [];
  const hasRails = currentRailPairs.some((p) => p.rail0 && p.rail1) || !!(currentEntry?.rail0 && currentEntry?.rail1);
  const partialLayers = Object.entries(layerRails).filter(([, entry]) => entry?.autoSatinPartial);

  return (
    <div style={{
      width: "100%",
      height: "100%",
      backgroundColor: "#f0f0f0",
      position: "relative",
      overflow: "hidden",
      overscrollBehavior: "none",
    }}>
      {partialLayers.length > 0 && (
        <div role="status" style={{ position: "absolute", bottom: 18, left: "50%", transform: "translateX(-50%)", zIndex: 65, maxWidth: "min(480px, 80%)", padding: "10px 16px", borderRadius: 12, background: "#fff7e6", color: "#704400", border: "1px solid #f0cf86", fontSize: 13, display: "flex", flexDirection: "column", gap: 8 }}>
          <div>
            <strong>Partial auto-digitizing result</strong>
            <div>Completed columns were kept. Review unfinished areas in layer{partialLayers.length === 1 ? "" : "s"} {partialLayers.map(([index]) => Number(index) + 1).join(", ")} before export. Original artwork is preserved.</div>
          </div>
          {currentEntry?.autoUnresolvedPieces?.length > 0 && (
            <button
              type="button"
              onClick={() => setSatinRetryRequest(Date.now())}
              style={{
                alignSelf: "flex-start",
                padding: "7px 12px",
                border: "0",
                borderRadius: 8,
                background: "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)",
                color: "#fff",
                font: "inherit",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Retry {unfinishedSatinLabel(currentEntry.autoUnresolvedPieces.length)}
            </button>
          )}
        </div>
      )}
      {/* AI loading overlay — blocks interaction until analysis completes */}
      {aiLoading && (
        <div data-tutorial-busy="analyzing-design" style={{
          position: "absolute", inset: 0, zIndex: 200,
          background: "rgba(240,240,240,0.82)",
          backdropFilter: "blur(4px)",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          gap: "16px",
          pointerEvents: "all",
        }}>
          <div style={{
            width: "48px", height: "48px", borderRadius: "50%",
            border: "3px solid rgba(124,58,237,0.15)",
            borderTop: "3px solid #7C3AED",
            animation: "stitch-ai-spin 0.8s linear infinite",
          }} />
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "15px", fontWeight: 700, color: "#111", fontFamily: "Clash Grotesk" }}>
              Analyzing design
            </div>
            <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "4px" }}>
              Setting up AI recommendations…
            </div>
          </div>
          <style>{`@keyframes stitch-ai-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Canvas — full area */}
      <StitchCanvas
        svgJoined={svgJoined}
        svgSplit={svgSplit}
        stitchSettings={stitchSettings}
        targetWidth={targetWidth}
        targetHeight={targetHeight}
        hoopWidth={hoopWidth}
        hoopHeight={hoopHeight}
        realStitchData={null}
        stitchToViewbox={null}
        isConverting={isConverting}
        convertStatus={convertStatus}
        onCancelConvert={cancelConversion}
        activeRailDrawing={activeRailDrawing}
        railEditing={railEditing}
        layerRails={previewRails}
        currentLayerIdx={currentLayerIdx}
        onAddRailPoint={addRailPoint}
        onUpdateLastRailPoint={updateLastRailPoint}
        onRemoveRailPoint={removeLastRailPoint}
        onFinishRail={finishRailDrawing}
        onCancelRail={cancelRailDrawing}
        onPreviewRailEdit={previewRailEdit}
        onCommitRailEdit={commitRailEdit}
        onInsertRailNode={insertRailNode}
        onStartRailEditing={startRailEditing}
        onSelectRailNodes={(selectedNodes) => setRailEditing((prev) => prev ? { ...prev, selectedNodes, selectedNode: selectedNodes.length === 1 ? selectedNodes[0] : null } : prev)}
        onSelectRailNode={(selectedNode) => setRailEditing((prev) => prev ? { ...prev, selectedNode, selectedNodes: [selectedNode] } : prev)}
        onDeleteRailNode={deleteSelectedRailNode}
        railSelection={railSelection}
        onSelectPathAsRail={selectPathAsRail}
        onCancelRailSelection={cancelRailSelection}
        activeRungDrawing={activeRungDrawing}
        onAddRungPoint={addRungPoint}
        currentRailPairs={currentRailPairs}
        onStartRungDrawing={guardedStartRungDrawing}
        onClearRungs={clearRungs}
        onRemoveRailPair={removeRailPair}
        colors={colors}
        embroidery={embroidery}
        layerStitchSettings={embroidery?.layerStitchSettings}
        aiAnalysis={aiAnalysis}
        aiLoading={aiLoading}
        layerPieces={layerPieces}
        autoDetectMeta={autoDetectMeta}
        onDragSplitPoint={handleDragSplitPoint}
        eraseActive={eraseActive}
        eraseLayerIdx={eraseLayerIdx}
        cutToolRef={cutToolRef}
        layerCutMarks={embroidery?.layerCutMarks}
        onEraseComplete={handleEraseComplete}
        onEraseCancel={handleEraseCancel}
        onCutMarksChange={handleCutMarksChange}
        onEraseDraftHistoryChange={handleEraseDraftHistoryChange}
        fillActive={fillActive}
        fillLayerIdx={fillLayerIdx}
        onFillApply={handleFillApply}
        fillToolRef={fillToolRef}
        pixelEraseActive={pixelEraseActive}
        pixelEraseLayerIdx={pixelEraseLayerIdx}
        onPixelEraseApply={handlePixelEraseApply}
        pixelEraseToolRef={pixelEraseToolRef}
        drawActive={drawActive}
        drawLayerIdx={drawLayerIdx}
        onDrawApply={handleDrawApply}
        drawToolRef={drawToolRef}
        morphActive={morphActive}
        morphLayerIdx={morphLayerIdx}
        onMorphApply={handleMorphApply}
        morphToolRef={morphToolRef}
        layerSelected={layerSelected}
        isPhone={isPhone}
        smartCutActive={smartCutActive}
        smartCutLayerIdx={smartCutLayerIdx}
        onSmartCutSubpath={handleSmartCutSubpath}
        satinEditingActive={stitchPanelOpen}
        referenceArtwork={referenceArtwork}
        referenceArtworkVisible={referenceArtworkVisible}
        referenceArtworkOpacity={referenceArtworkOpacity}
        onToggleReferenceArtwork={() => setReferenceArtworkVisible((visible) => !visible)}
        onReferenceArtworkOpacityChange={setReferenceArtworkOpacity}
        onRetraceArtwork={onRetraceArtwork}
      />

      {/* Floating center toolbar (Excalidraw-style) — bottom-anchored on phones */}
      <StitchToolbar
        stitchSettings={stitchSettings}
        setStitchSettings={setStitchSettings}
        embroidery={embroidery}
        currentLayerIdx={currentLayerIdx}
        colors={colors}
        aiAnalysis={aiAnalysis}
        currentLayerIsSatin={currentLayerIsSatin}
        autoSatinMmPerUnit={((targetWidth / originalDimensions.width) + (targetHeight / originalDimensions.height)) / 2}
        autoSatinJobRevision={layerRails?.[currentLayerIdx] || null}
        autoSatinJobContext={JSON.stringify([fileId || "unsaved", currentLayerIdx])}
        satinRetryRequest={satinRetryRequest}
        onSatinRetryRequestHandled={() => setSatinRetryRequest(null)}
        autoSatinPath={autoSatinPath}
        autoSatinRevision={layerRails}
        onAutoSatinPreview={(result) => setAutoSatinPreview({ layer: currentLayerIdx, path: autoSatinPath, revision: layerRails, result })}
        onAutoSatinDiscard={() => setAutoSatinPreview(null)}
        onAutoSatinApply={(result, source) => {
          const currentSnapshot = JSON.stringify({
            pathD: autoSatinPath,
            mmPerUnit: ((targetWidth / originalDimensions.width) + (targetHeight / originalDimensions.height)) / 2,
            revision: layerRails?.[currentLayerIdx] || null,
          });
          if (readOnly || !currentLayerIsSatin || !source || source.snapshot !== currentSnapshot)
            throw new Error("The layer changed during generation. Run Auto again for the current layer.");
          if (!result?.pairs?.length)
            throw new Error("No validated rail pairs were returned.");
          updateLayerRailsWithHistory(prev => ({
            ...prev,
            [currentLayerIdx]: nextLayerRailsFromSatinResult(
              prev[currentLayerIdx],
              result,
              { merge: Boolean(source.retryUnresolved) }
            ),
          }));
          setAutoSatinPreview(null);
        }}
        onApplySatinMode={applySatinMode}
        onCreateSatinBorder={handleCreateSatinBorder}
        activeRailDrawing={activeRailDrawing}
        railEditing={railEditing}
        hasRails={hasRails}
        railPairCount={currentRailPairs.filter((p) => p.rail0 && p.rail1).length || (currentEntry?.rail0 && currentEntry?.rail1 ? 1 : 0)}
        currentRailPairs={currentRailPairs}
        onStartRailDrawing={withLeaveCut(startRailDrawing)}
        onFinishRail={finishRailDrawing}
        onCancelRail={cancelRailDrawing}
        onStartRailEditing={startRailEditing}
        onFinishRailEditing={() => setRailEditing(null)}
        onDeleteRailNode={deleteSelectedRailNode}
        onReverseRailColumn={reverseRailColumn}
        onCurveRailEdge={curveRailEdge}
        onSetRailNodeType={setSelectedRailNodeType}
        onClearRails={clearLayerRails}
        railSelection={railSelection}
        onStartRailSelection={withLeaveCut(startRailSelection)}
        onCancelRailSelection={cancelRailSelection}
        activeRungDrawing={activeRungDrawing}
        onStartRungDrawing={guardedStartRungDrawing}
        onFinishRungDrawing={finishRungDrawing}
        onClearRungs={clearRungs}
        rungCount={currentRailPairs.reduce((sum, p) => sum + (p.rungs?.length || 0), 0)}
        svgJoined={svgJoined}
        onSplitRailsCreated={handleSplitRailsCreated}
        onUndoLastSplitPair={handleUndoLastSplitPair}
        onCutFinished={handleCutFinished}
        layerPieces={layerPieces}
        canUndo={activeRailDrawing ? canUndoRailPoint : (!!embroidery?.canUndoStitch || (eraseActive && eraseDraftHistory.canUndo))}
        canRedo={activeRailDrawing ? canRedoRailPoint : (!!embroidery?.canRedoStitch || (eraseActive && eraseDraftHistory.canRedo))}
        onUndo={handleUndo}
        onRedo={handleRedo}
        layerSelected={layerSelected}
        onLayerChange={handleLayerChange}
        onAddSublayer={handleAddSublayer}
        layerMeta={layerMeta}
        onEraseStart={guardedEraseStart}
        eraseActive={eraseActive}
        onEraseCancel={handleEraseCancel}
        onFillStart={guardedFillStart}
        fillActive={fillActive}
        onColorChange={handleColorChange}
        onPixelEraseStart={guardedPixelEraseStart}
        pixelEraseActive={pixelEraseActive}
        onDrawStart={guardedDrawStart}
        drawActive={drawActive}
        onMorphStart={guardedMorphStart}
        morphActive={morphActive}
        onStitchPanelOpenChange={setStitchPanelOpen}
        smartCutActive={smartCutActive}
        onSmartCutStart={guardedSmartCutStart}
        onSmartCutCancel={handleSmartCutCancel}
        leaveCutTool={leaveCutTool}
        onOpenExport={openExportModal}
        aiLoading={aiLoading}
        targetWidth={targetWidth}
        targetHeight={targetHeight}
        setTargetWidth={setTargetWidth}
        setTargetHeight={setTargetHeight}
        lockAspectRatio={lockAspectRatio}
        setLockAspectRatio={setLockAspectRatio}
        originalDimensions={originalDimensions}
        placement={isPhone ? "bottom" : "top"}
        isPhone={isPhone}
      />

      {/* Export button portaled into the topbar slot */}
      {topbarExportSlot && createPortal(
          <StitchExportButton
            selectedFormat={selectedFormat}
            setSelectedFormat={handleFormatChange}
            selectedFabric={selectedFabric}
            setSelectedFabric={setSelectedFabric}
            stitchSettings={stitchSettings}
            setStitchSettings={setStitchSettings}
            embroidery={embroidery}
            fileName={fileName}
            layerSelected={layerSelected}
            isConverting={isConverting}
            conversionResult={conversionResult}
            onConvert={() => handleConvert(selectedFormat)}
            onDownload={handleDownload}
            onCancelConvert={cancelConversion}
            aiAnalysis={aiAnalysis}
            aiLoading={aiLoading}
            svgJoined={svgJoined}
            targetWidth={targetWidth}
            targetHeight={targetHeight}
            selectedHoop={selectedHoop}
            setSelectedHoop={setSelectedHoop}
            setTargetWidth={setTargetWidth}
            setTargetHeight={setTargetHeight}
            lockAspectRatio={lockAspectRatio}
            setLockAspectRatio={setLockAspectRatio}
            originalDimensions={originalDimensions}
            stitchFileSaveStatus={stitchFileSaveStatus}
            savedFileMeta={savedFileMeta}
            exportOpen={exportModalOpen}
            onExportOpenChange={setExportModalOpen}
          />,
          topbarExportSlot,
        )}

      {/* Phone: floating "Layers" pill that toggles a slide-down drawer.
          Mirrors the SVG editor's Procreate-style mobile pattern. */}
      {isPhone && colors && colors.length > 0 && (
        <>
          <button
            onClick={() => setLayersPanelOpen((prev) => !prev)}
            style={{
              position: "absolute",
              // Sits just below the top-right zoom pill (zoom is at top: 8px,
              // ~34px tall). Mirrors the SVG editor's mobile stack.
              top: "50px",
              right: "12px",
              zIndex: 110,
              display: "flex",
              alignItems: "center",
              gap: "5px",
              padding: "6px 12px",
              background: layersPanelOpen ? "#333" : "rgba(255,255,255,0.95)",
              color: layersPanelOpen ? "#fff" : "#555",
              border: "1px solid",
              borderColor: layersPanelOpen ? "#333" : "rgba(0,0,0,0.08)",
              borderRadius: "8px",
              fontSize: "11px",
              fontWeight: 600,
              fontFamily: "Clash Grotesk",
              cursor: "pointer",
              boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              pointerEvents: "auto",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
              <path d="M12 2L2 7l10 5 10-5-10-5z" fill={layersPanelOpen ? "#fff" : "#888"} opacity="0.3" />
              <path d="M2 12l10 5 10-5" stroke={layersPanelOpen ? "#fff" : "#888"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              <path d="M2 17l10 5 10-5" stroke={layersPanelOpen ? "#fff" : "#888"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
            Layers
            <svg width="8" height="5" viewBox="0 0 8 5" fill="none" style={{
              transform: layersPanelOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}>
              <path d="M1 1l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {layersPanelOpen && (
            <div
              onClick={() => setLayersPanelOpen(false)}
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 105,
                background: "rgba(0,0,0,0.15)",
              }}
            />
          )}
        </>
      )}

      {/* Right-side layer panel — desktop always renders the side panel; on
          phones we render an absolutely-positioned dropdown anchored under
          the Layers pill that animates open/closed. */}
      <StitchSidebar
        colors={colors}
        embroidery={embroidery}
        layerSelected={layerSelected}
        onLayerChange={handleLayerChange}
        setLayerSelected={setLayerSelected}
        onReorderLayer={handleReorderLayer}
        onDeleteLayer={handleDeleteLayer}
        onDuplicateLayer={handleDuplicateLayer}
        onAddSublayer={handleAddSublayer}
        layerMeta={layerMeta}
        onColorChange={handleColorChange}
        aiAnalysis={aiAnalysis}
        aiLoading={aiLoading}
        aiError={aiError}
        isPhone={isPhone}
        isOpen={isPhone ? layersPanelOpen : true}
        onClose={() => setLayersPanelOpen(false)}
      />

      <SatinJobQueue
        fileId={fileId || "unsaved"}
        onSelectLayer={(index) => setLayerSelected?.(index)}
      />


      {/* Error toast */}
      {error && (
        <div style={{
          position: "absolute",
          bottom: "16px",
          left: "50%",
          transform: "translateX(-50%)",
          background: "#fef2f2",
          border: "1px solid #fca5a5",
          borderRadius: "10px",
          padding: "8px 16px",
          fontSize: "12px",
          color: "#dc2626",
          fontFamily: "Clash Grotesk, sans-serif",
          fontWeight: 500,
          zIndex: 20,
          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
        }}>
          {error}
        </div>
      )}

      {showToolbarPaywall && (
        <PaywallModal
          isVisible={showToolbarPaywall}
          onClose={() => setShowToolbarPaywall(false)}
          onSuccess={() => {
            setShowToolbarPaywall(false);
            pendingExportRef.current?.();
            pendingExportRef.current = null;
          }}
          action="export"
          isAuthenticated={!!user}
          isPremium={!!effectivePremium}
        />
      )}
      {showToolbarIOSPaywall && (
        <IOSPaywall
          isOpen={showToolbarIOSPaywall}
          onClose={() => setShowToolbarIOSPaywall(false)}
          onSuccess={() => {
            setShowToolbarIOSPaywall(false);
            pendingExportRef.current?.();
            pendingExportRef.current = null;
          }}
        />
      )}
    </div>
  );
};

export default StitchEditor;
